import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import FlagIcon from "@mui/icons-material/Flag";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import GavelIcon from "@mui/icons-material/Gavel";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import { useTranslation } from "i18n-fe";
import {
  resolveScoreSheetTotals,
  formatPercentage,
  // #1081 — raw-score points (earned AND possible-max alike) render through
  // this test-aware formatter so a freestyle test's totals show two
  // decimals and every other test shows one, instead of a hardcoded
  // toFixed(1) everywhere a points total is displayed.
  formatPoints,
  REMARKS_MAX_PER_MOVEMENT,
  REMARKS_MAX_GENERAL,
  REMARKS_COUNTER_THRESHOLD,
  SCORE_MAX,
  // #889 — score step (0.5 vs 0.1) resolves per mark: young-horse tests are
  // tenths across the whole sheet, freestyle only on artistic movements.
  scoreStepFor,
  // #683 — error-of-course tier selection (points/percent/eliminate) lives
  // in score-common so it's Jest-testable and shared with any other score
  // FE that ever needs it. #773 — resolveErrorTiers prefers the class's
  // admin-declared scheme over the catalog test's errorRule.
  resolveErrorTiers,
  errorTierForOccurrence,
  // #885 — a scribe unassigned to this class can still view a FINAL ride's
  // scoresheet read-only; DERIVED_RIDE_STATUSES.FINAL is the gate.
  DERIVED_RIDE_STATUSES,
  // #916 (part A) — Scratch / No Show gavel-menu items call score-api's
  // setRideStatus with status SCRATCHED + one of these literal term reasons.
  RIDE_STATUSES,
  RIDE_TERM_REASONS,
  // Card #1992 — the ride-level No Show is restricted to the judge seated
  // at position C ("presiding judge"). Same constant score-api's handler
  // gates on server-side; imported here only to render/gate the menu item,
  // never as the authorization itself (server enforces it independently).
  PRESIDING_JUDGE_POSITION,
  // #752 — undoing/removing an error of course must re-tier the ones that
  // remain (a removed 1st error means the old 2nd error becomes the 1st,
  // and must drop to the 1st-tier deduction). retierErrors is the single
  // source of truth for recomputing points/percent/label from position; it
  // shares the "Nth error of course" label formatting (ordinal) that
  // recordError below uses too.
  retierErrors,
  ordinal,
  // #1755 — shared with scoreadmin-fe's RideEditor so both surfaces group a
  // freestyle/young-horse test's movements under the identical section
  // header key instead of each keeping its own copy.
  FREESTYLE_SECTION_LABEL_KEY,
  // #1991 — the SAME judge-topology helper scoreboard-mode-payload.js uses
  // to decide "does this class have panel judging" ((count || 0) > 1) —
  // never a locally-recomputed judge count, which would drift from every
  // other panel-ness check in the codebase.
  expectedJudgeCountForClass,
  // #2256 — USEF DR203.14 (docs/rules/usef/rulebook-dr-dressage-2026.pdf):
  // the person leading the horse in a DSHB in-hand/group/championship class
  // is a Handler, not a Rider. Reuses classifyDshbClassKind's className/
  // fvdTestId signal (for Championship, which carries no Test def of its
  // own) plus the resolved Test row's hasRideTiming flag (for the plain
  // in-hand/group/handler-sheet family, #2188) rather than inventing a new
  // one — see that function's own doc comment in dshb-class-kind.js.
  isDshbHandlerClass,
} from "score-common";
// #915 / #1664 — the ride's test NAME (for the sticky-header chip), resolved
// off ride.testId, table cache preferred over the bundled catalog (see
// classDisplay.js's rideTestName/resolveCatalogTestName). Separate from the
// `test` object assembled by useScoreSheet above (which is what's actually
// scored against and already required for the form to function) — a
// hydration failure here can only ever affect this display name, never the
// scoring path itself.
import AppShell from "./AppShell";
import DirectiveAnnotation from "./DirectiveAnnotation";
import AnnotatableText from "./AnnotatableText";
import { useScoreContext } from "../context/ScoreContext";
import { useAssignedPosition } from "../hooks/useAssignedPosition";
import { useScoreSheet } from "../hooks/useScoreSheet";
import {
  useSheetFieldNav,
  markFieldKey,
  commentFieldKey,
  GENERAL_REMARKS_KEY,
  MARK_FIRST,
  COMMENT_FIRST,
} from "../hooks/useSheetFieldNav";
import {
  getSheetFieldOrder,
  setSheetFieldOrder,
} from "../store/sheetFieldOrder";
import { useRideUtilityEvents } from "../hooks/useRideUtilityEvents";
import { useRideNoShow } from "../hooks/useRideNoShow";
import { useRideExcused } from "../hooks/useRideExcused";
import {
  UTILITY_EVENT_TYPE_LIST,
  UTILITY_EVENT_TYPE_BY_KEY,
} from "../util/utilityEventTypes";
import {
  useApiResource,
  SignatureBox,
  ScoreEntry,
  // Card #1992 AC7 — noShow.at is a genuine server instant (new
  // Date().toISOString(), not an FVD Access stamp), so it renders through
  // common-fe's formatVenueTime (the show's real venueTimezone), the same
  // family classDisplay.js's local formatVenueTime is NOT: that one is
  // reserved for FVD-sourced scheduledTime/rideTime (#1811). scribe-fe has
  // no show.venueTimezone plumbed into ScoringForm yet, so this call omits
  // `timeZone` and falls back to the Pacific default — same fallback every
  // other scribe-fe time display already assumes.
  formatVenueTime,
} from "common-fe";
import { colors } from "brand";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useCatalogDefs } from "../hooks/useCatalogDefs";
import { rideCnoNumber } from "../util/rideDisplay";
import { classNumberLabel, rideTestName } from "../util/classDisplay";
import { normalizeRemarks } from "../util/remarks";
import { useSheetSignature } from "../hooks/useSheetSignature";
import { useShowJudges } from "../hooks/useShowJudges";
import { useRidesForClass } from "../hooks/useRidesForClass";
import { useOtherSectionSubmission } from "../hooks/useOtherSectionSubmission";
import {
  hasSectionJudgeAssignments,
  isSectionEditableForBooth,
  resolvePositionForSection,
} from "../util/sectionJudgeGating";
import { judgedPositionsFor } from "../util/scribeAssignment";

// #208 — preset elimination / movement-error reasons offered by the
// per-row menu. "Custom reason…" lets the scribe type a free-text reason
// when none of the presets fit. Marking any of these eliminates the
// whole sheet (bypasses the zero/missing/flag validation at sign time).
// #2063 — "Withdrawn / retired" removed: it's one of DR123.3's own outcome
// labels (Scratched), not a DR124 elimination cause, so it doesn't belong in
// a cause-only pick list (score-common's constants.js has the full citation
// and untangling rationale — this array is scribe-fe's own hand-mirrored
// copy, see the #1756 comment above). A scribe who needs to record a
// withdrawal now uses the "Scratch ride" section of this same gavel menu
// (below, #916 part A) instead of eliminating with this cause.
const ELIMINATION_REASONS = [
  "Errors of course",
  "Off course",
  // #753 — split from the old combined "Fall of horse or rider" preset so
  // the scribe can record either (or both, as two separate entries).
  "Horse Fall",
  "Rider Fall",
  "Lameness",
  "Blood",
  "Equipment violation",
];

// #221 — local id for individually-removable eliminations / deductions.
const makeId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// #675 — a freestyle rider chooses their own movement order, so the
// technical requirements the scribe scores against are still listed in
// catalog order (walk items, then trot, then canter — see
// docs/score/2023_tests/Freestyle_*.json), which doesn't match the order the
// ride is actually performed in. Sub-group the TECHNICAL rows by gait so the
// scribe can jump straight to the right cluster. The catalog carries no
// explicit gait field (catalog-enrich's mapFreestyleMovements only emits
// type/section/movementNumber/description/directive/location/coefficient),
// so derive it from the movement's description text. First keyword match
// wins; a movement that names no gait (halts, salutes, "change of lead
// through", generic transitions) falls into "other" instead of vanishing.
const GAIT_KEYWORD_PATTERNS = [
  ["walk", /\bwalk\b/i],
  ["trot", /\btrot\b/i],
  ["canter", /\bcanter\b/i],
];
function deriveMovementGait(description) {
  const text = description ?? "";
  for (const [gait, pattern] of GAIT_KEYWORD_PATTERNS) {
    if (pattern.test(text)) return gait;
  }
  return "other";
}
// Static i18n keys only (no dynamic key interpolation) for the gait divider.
const GAIT_LABEL_KEY = {
  walk: "scoring.gaitWalk",
  trot: "scoring.gaitTrot",
  canter: "scoring.gaitCanter",
  other: "scoring.gaitOther",
};

// #542 — young-horse / Children T/Q tests split into a Technical section (judged
// at C) and a Quality section (judged at E/B). The class carries the admin-
// declared topology (Class.qualityJudging, card #542). Filter the test's
// movements to what THIS scribe/judge should see AND score:
//   - no quality section, or no topology set → all movements (legacy behavior).
//   - "technical-only" → drop the quality section for everyone.
//   - "two-judge" → the judge at the quality position sees ONLY the quality
//                   section; everyone else (the technical judge at C) sees ONLY
//                   the technical (non-quality) movements.
// Used for BOTH the render loop and the completion-gate walk so a section this
// judge doesn't score is never flagged as a missing movement.
export function visibleMovementsFor(test, klass, judgePosition) {
  const movements = test?.movements ?? [];
  const topology = klass?.qualityJudging?.topology;
  const hasQuality = movements.some((m) => m.section === "quality");
  if (!hasQuality || !topology) return movements;
  if (topology === "technical-only") {
    return movements.filter((m) => m.section !== "quality");
  }
  // two-judge
  const qualityPosition = klass?.qualityJudging?.qualityPosition;
  const isQualityJudge = Boolean(qualityPosition) && judgePosition === qualityPosition;
  return movements.filter((m) =>
    isQualityJudge ? m.section === "quality" : m.section !== "quality"
  );
}

// Describe a penalty tier for menu labels. #683 — a tier is now points,
// percent (FEI), or elimination.
const describeTier = (tier) =>
  !tier
    ? ""
    : tier.eliminate
      ? "elimination"
      : tier.percent !== undefined
        ? `−${tier.percent}%`
        : `−${tier.points} pt`;

// #214 — render the pipe character "|" as an in-cell line break, so
// test cells (instruction, directives, position) can carry multi-line
// values from import data (e.g. Location "A|X") and display each segment
// on its own line. Empty segments are dropped; a value with no pipe
// renders as a single line unchanged.
function PipeText({ text, variant = "body2", sx }) {
  const value = text ?? "";
  const lines = value
    .split("|")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (lines.length === 0) return null;
  return (
    <Typography variant={variant} component="div" sx={sx}>
      {lines.map((line, i) => (
        <Box component="span" key={i} sx={{ display: "block" }}>
          {line}
        </Box>
      ))}
    </Typography>
  );
}

// #2080: a field "has an annotation to clear" once its annotated* string
// is non-empty. This is a plain-string check on purpose, not a re-parse of
// the markup (parseAnnotated/groupRuns), #2079's guidance for this card is
// that AnnotatedTokens/DirectiveAnnotation/AnnotatableText stay
// markup-agnostic, and clear-related decisions live in ScoringForm instead,
// so this deliberately doesn't reach back into that parsing. It also means
// the clear button is exactly the mirror of the existing "does this field
// render from annotated or fall back to plain" check every one of these
// components already does, so "there's a clear button" and "the field is
// rendering annotated markup" agree everywhere.
function hasAnnotationMarks(annotated) {
  return typeof annotated === "string" && annotated.trim().length > 0;
}

// #2080: small icon button rendered beside an annotatable field, only when
// that field actually carries stored annotation markup (hasAnnotationMarks
// above), nothing to accidentally tap when a field is still plain. Tapping
// it never clears directly (AC2): it hands (label, onConfirm) to the one
// shared confirm dialog below, which is what actually resets the field.
function AnnotationClearButton({
  annotated,
  disabled,
  label,
  onConfirm,
  onRequestClear,
}) {
  const { t } = useTranslation("score");
  if (!hasAnnotationMarks(annotated)) return null;
  const clearLabel = t("scoring.clearAnnotations", { label });
  return (
    <Tooltip title={clearLabel}>
      <span>
        <IconButton
          size="small"
          tabIndex={-1} /* #535: secondary control, out of the mark tab flow */
          disabled={disabled}
          aria-label={clearLabel}
          data-testid="annotation-clear-button"
          onClick={() => onRequestClear(label, onConfirm)}
          sx={{ p: 0.25 }}
        >
          <FormatClearIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

// #317 — single collapsible "Test Overview" box. The per-test prose items
// (Purpose, Introduce, and room for more) are combined into ONE box that
// expands to show every item, splitting the width horizontally by the
// number of items present (one item = full width, two = half each, …).
// Renders nothing when the test has no overview content. Replaces the old
// per-field boxes (#213/#257).
// #310 — each item's prose is word-cycle annotatable like movement text:
// tap words to highlight/underline/circle them; the annotated markdown is
// stored on the score sheet (annotated*) via the item's onChange.
function TestOverviewBox({ title, items, disabled, onRequestClear }) {
  if (!items || items.length === 0) return null;
  return (
    <Accordion
      disableGutters
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{
          minHeight: 0,
          px: 1.25,
          "& .MuiAccordionSummary-content": { my: 0.5 },
        }}
      >
        <Typography variant="caption" fontWeight={700}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: 1.25, pb: 1 }}>
        {/* #317 — items share one row, dividing the width evenly; stretch
            so they stay equal-height (the #257 symmetry) regardless of how
            much prose each holds. Stacks vertically on very small screens. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems="stretch"
          divider={<Divider orientation="vertical" flexItem />}
        >
          {items.map((item) => (
            <Box key={item.key} sx={{ flex: 1, minWidth: 0 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 0.25 }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  display="block"
                >
                  {item.label}
                </Typography>
                {/* #2080: clear control beside the field, only rendered
                    once the field actually carries annotation markup. */}
                <AnnotationClearButton
                  annotated={item.annotated}
                  disabled={disabled}
                  label={item.label}
                  onConfirm={() => item.onChange("")}
                  onRequestClear={onRequestClear}
                />
              </Box>
              <AnnotatableText
                plain={item.content}
                annotated={item.annotated}
                disabled={disabled}
                onChange={item.onChange}
                sx={{ color: "text.secondary", lineHeight: 1.6 }}
              />
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// #208 / #221 — sheet-level penalties banner. Lists each elimination
// reason (red) and each error-of-course point penalty (amber) as a chip
// with its own X (like the utility-event chips), removable individually.
// The banner reads "Eliminated" once any elimination is present;
// otherwise "Penalties" (errors deduct points but don't eliminate).
function PenaltiesBanner({
  eliminations,
  errors,
  onRemoveElimination,
  onRemoveError,
  disabled,
}) {
  const eliminated = eliminations.length > 0;
  // #221 — once eliminated, everything reads red on the paper background
  // (the first error penalty stops being amber). Plain penalties (no
  // elimination yet) stay amber.
  const accent = eliminated ? "error.main" : "warning.main";
  const errorChipColor = eliminated ? "error" : "warning";
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: 1,
        borderColor: accent,
        bgcolor: "background.paper",
        color: accent,
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ mr: 0.5, color: accent }}
      >
        {eliminated ? "Eliminated" : "Penalties"}
      </Typography>
      {eliminations.map((e) => (
        <Chip
          key={e.id}
          size="small"
          variant="outlined"
          color="error"
          label={`${e.reason}${
            e.movementNumber ? ` · mvt ${e.movementNumber}` : ""
          }`}
          // #754 — Blood is a welfare flag: once indicated it can never be
          // undone, unlike every other elimination reason. No delete
          // affordance is offered for it.
          onDelete={
            disabled || e.reason === "Blood"
              ? undefined
              : () => onRemoveElimination(e.id)
          }
        />
      ))}
      {errors.map((er) => (
        <Chip
          key={er.id}
          size="small"
          variant="outlined"
          color={errorChipColor}
          // #683 — an error entry carries either `points` or `percent`
          // (FEI), never both.
          label={`−${er.percent !== undefined ? `${er.percent}%` : `${er.points} pt`} · ${er.label}`}
          onDelete={disabled ? undefined : () => onRemoveError(er.id)}
        />
      ))}
    </Box>
  );
}

// #221 — dark-mode contrast: the focused outline + floating label of an
// outlined TextField resolve to the dim brand primary, so an active field
// reads grey-on-grey on dark dialog surfaces. Force both to text.primary.
// (Mirrors READABLE_TEXTFIELD_SX in RideList; see watch-color-visibility.)
// #1755 — ScoreInput/ScoreEntry (score-entry field + coefficient badge box)
// moved to common-fe/ScoreInput so scoreadmin-fe's RideEditor shares the
// exact same typed/snap-on-blur behavior; see that file for the SCORE_ENTRY_WIDTH/
// SCORE_FIELD_WIDTH doc this code used to carry locally.
const READABLE_TEXTFIELD_SX = {
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "text.primary",
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "text.primary" },
};

// #261 — horse identity chip. Tapping the chip opens a popover with the
// horse's core bio (breed, height, sex, color). Tap-based (not hover) so
// it works on mobile/tablet. The popover only offers itself when there's
// bio to show (mock rides carry it; emergency manual adds don't).
function HorseChip({ horse }) {
  const [anchorEl, setAnchorEl] = useState(null);
  if (!horse) return null;
  const bio = [
    ["Breed", horse.breed],
    ["Height", horse.height],
    ["Sex", horse.sex],
    ["Color", horse.color],
  ].filter(([, v]) => v);
  const hasBio = bio.length > 0;
  return (
    <>
      <Chip
        data-testid="horse-chip"
        label={`Horse: ${horse.name}`}
        size="small"
        variant="outlined"
        clickable={hasBio}
        onClick={hasBio ? (e) => setAnchorEl(e.currentTarget) : undefined}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, minWidth: 168 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            {horse.name}
          </Typography>
          <Stack spacing={0.25}>
            {bio.map(([label, value]) => (
              <Typography
                key={label}
                variant="caption"
                data-testid={`horse-bio-${label.toLowerCase()}`}
              >
                <Box
                  component="span"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  {label}:
                </Box>{" "}
                {value}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}

// R1-UI-020 / card #139 — Utility panel. Raising an event is a QUEUED write
// (server-persisted on the Ride, offline-safe via the outbox — see
// useRideUtilityEvents) that every scribe/judge terminal scoring the SAME
// ride picks up by polling — real cross-terminal delivery, not a local mock.
// R1-UI-020 (#124) — the dropdown trigger lives flush-right of the
// ride-header rows. Empty state shows nothing else; only active events
// surface in the banner below.
function UtilityTrigger({ rideId, disabled, onRaise, isPanel }) {
  const [anchorEl, setAnchorEl] = useState(null);
  // #1991 AC1 — a `panelOnly` menu entry (MEETING_REQUESTED) only appears in
  // classes with panel judging; every other entry (BLOOD/LAMENESS) is
  // unaffected and always shows.
  const menuItems = UTILITY_EVENT_TYPE_LIST.filter((t) => !t.panelOnly || isPanel);
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        endIcon={<ArrowDropDownIcon />}
        disabled={disabled || !rideId}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ minWidth: 88 }}
      >
        Utility
      </Button>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menuItems.map((t) => (
          <MenuItem
            key={t.key}
            onClick={() => {
              onRaise(t.key);
              setAnchorEl(null);
            }}
          >
            {t.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// Active-events strip. Renders nothing when there are no active events for
// this ride. Each active event is a Chip; the X only shows for the scribe
// who raised it — card #139 folds in #153's "gate the X" decision now that
// events carry a real raisedByScribeId (the server independently enforces
// originator-or-admin on DELETE regardless of what this app shows).
function UtilityBanner({ events, disabled, mySub, onClear }) {
  const { t } = useTranslation("score");
  if (events.length === 0) return null;
  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "warning.main",
        borderRadius: 2,
      }}
    >
      {/* #205 — header so the alerts box doesn't read as an orphaned
          row of chips. Only renders when the box does (events > 0). */}
      <Typography
        data-testid="active-alerts-header"
        variant="overline"
        sx={{
          display: "block",
          mb: 1,
          fontWeight: 700,
          lineHeight: 1.2,
          // color contrast (dark mode): warning.main reads ok here on
          // paper, but keep the label firmly legible.
          color: "warning.main",
        }}
      >
        {t("scoring.activeAlerts")}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flexWrap: "wrap" }}
      >
        {events.map((e) => {
          const cfg = UTILITY_EVENT_TYPE_BY_KEY[e.type];
          const isMine = Boolean(mySub) && e.raisedByScribeId === mySub;
          const canClear = !disabled && !e._pending && isMine;
          return (
            <Chip
              key={e.id}
              label={
                <Stack
                  component="span"
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                >
                  <span>{cfg?.label ?? e.type}</span>
                  <span style={{ opacity: 0.65, fontSize: 11 }}>
                    {e._pending
                      ? t("scoring.utilitySyncing")
                      : new Date(e.raisedAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                  </span>
                </Stack>
              }
              color={cfg?.severity ?? "warning"}
              onDelete={canClear ? () => onClear(e.id) : undefined}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

// Card #1992 AC7/AC8/AC9/AC11 — the ride-level No Show alert. Renders on
// EVERY panel position's sheet for this ride while ride.noShow is active
// (delivered by useRideNoShow's poll, same cross-terminal-delivery pattern
// as UtilityBanner/useRideUtilityEvents above), so it satisfies "every
// OTHER panel judge sheet" for the E/M/B/H positions and doubles as C's own
// confirmation that the mark landed.
//
// AC7 "who did it" — the server stamps `by` as a raw Cognito sub, which is
// not a name a scribe should ever see rendered. `byPosition` ("C", or null
// if an admin who wasn't seated at C marked it — see #1992 fix in
// score-api's markRideNoShow) is what's shown: resolved to the actual
// judge's name via Class.judgeAssignments[position] (the same FVD-provided
// name lookup #761's section-judge gating already uses), falling back to a
// bare "Judge <position>" if the class carries no name for that seat, and
// "an administrator" only for the rare admin-without-a-seat case. Never a
// raw sub.
//
// AC11 "Undo No Show" — only rendered for the position that could have
// caused it (C): the server enforces the same C-only gate on the DELETE as
// the POST, so an E/M/B/H scribe would only see it fail.
function NoShowAlertBanner({ noShow, klass, judgePosition, onUndo, undoing, undoError }) {
  const { t } = useTranslation("score");
  if (!noShow?.active) return null;
  const canUndo = judgePosition === PRESIDING_JUDGE_POSITION;
  const byName = noShow.byPosition
    ? klass?.judgeAssignments?.[noShow.byPosition]?.[0] ??
      t("scoring.noShowByPositionFallback", { position: noShow.byPosition })
    : t("scoring.noShowByUnknown");
  const whenLabel = formatVenueTime(noShow.at);
  return (
    <Box
      data-testid="no-show-alert-banner"
      sx={{
        mb: 2,
        p: 1.5,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "error.main",
        borderRadius: 2,
      }}
    >
      <Typography
        data-testid="no-show-alert-header"
        variant="overline"
        sx={{
          display: "block",
          mb: 0.5,
          fontWeight: 700,
          lineHeight: 1.2,
          color: "error.main",
        }}
      >
        {t("scoring.noShowAlertHeader")}
      </Typography>
      <Typography variant="body2" sx={{ mb: canUndo ? 1 : 0 }}>
        {t("scoring.noShowAlertBody", { who: byName, when: whenLabel })}
      </Typography>
      {undoError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {t("scoring.gavelRideNoShowUndoError")}
        </Alert>
      )}
      {canUndo && (
        <Button
          size="small"
          variant="outlined"
          color="error"
          loading={undoing}
          onClick={onUndo}
          data-testid="no-show-undo-button"
        >
          {t("scoring.noShowUndoButton")}
        </Button>
      )}
    </Box>
  );
}

// Card #2064 — the ride-level Excused alert (DR123.3's fourth term reason),
// modeled directly on NoShowAlertBanner just above. Renders on EVERY panel
// position's sheet for this ride while ride.excused is active (delivered by
// useRideExcused's poll, same cross-terminal-delivery pattern), so it
// satisfies "every OTHER panel judge sheet" for the E/M/B/H positions and
// doubles as C's own confirmation that the mark landed.
//
// "who did it" and "Undo" follow the exact same rules as No Show: byPosition
// resolves to the judge's name via Class.judgeAssignments, and only C (the
// position that could have caused it) sees the Undo button.
function ExcusedAlertBanner({ excused, klass, judgePosition, onUndo, undoing, undoError }) {
  const { t } = useTranslation("score");
  if (!excused?.active) return null;
  const canUndo = judgePosition === PRESIDING_JUDGE_POSITION;
  const byName = excused.byPosition
    ? klass?.judgeAssignments?.[excused.byPosition]?.[0] ??
      t("scoring.noShowByPositionFallback", { position: excused.byPosition })
    : t("scoring.noShowByUnknown");
  const whenLabel = formatVenueTime(excused.at);
  return (
    <Box
      data-testid="excused-alert-banner"
      sx={{
        mb: 2,
        p: 1.5,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "error.main",
        borderRadius: 2,
      }}
    >
      <Typography
        data-testid="excused-alert-header"
        variant="overline"
        sx={{
          display: "block",
          mb: 0.5,
          fontWeight: 700,
          lineHeight: 1.2,
          color: "error.main",
        }}
      >
        {t("scoring.excusedAlertHeader")}
      </Typography>
      <Typography variant="body2" sx={{ mb: canUndo ? 1 : 0 }}>
        {t("scoring.excusedAlertBody", { who: byName, when: whenLabel })}
      </Typography>
      {undoError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {t("scoring.gavelRideExcusedUndoError")}
        </Alert>
      )}
      {canUndo && (
        <Button
          size="small"
          variant="outlined"
          color="error"
          loading={undoing}
          onClick={onUndo}
          data-testid="excused-undo-button"
        >
          {t("scoring.excusedUndoButton")}
        </Button>
      )}
    </Box>
  );
}

function RemarksInput({
  value,
  onChange,
  disabled,
  placeholder,
  maxLength = REMARKS_MAX_PER_MOVEMENT,
  minRows = 1,
  inputRef,
  onKeyDown,
  testId,
}) {
  // #669 — normalize on display too: a value stored before this rule
  // existed (or written by an older client) may already contain a newline.
  const v = normalizeRemarks(value ?? "");
  // R1-UI-012 — counter appears once we're within the threshold.
  const showCounter = v.length >= maxLength - REMARKS_COUNTER_THRESHOLD;
  return (
    <TextField
      data-testid={testId}
      size="small"
      placeholder={placeholder}
      value={v}
      onChange={(e) => {
        // #669 — the single value-boundary: strip newlines from every write,
        // however they arrived (typing, paste of multi-line clipboard text,
        // IME, drag-drop). Ordinary typing has no newline and passes through
        // unchanged (see normalizeRemarks).
        const next = normalizeRemarks(e.target.value);
        // Hard cap — maxLength on inputProps blocks paste-overflow too.
        onChange(next.length > maxLength ? next.slice(0, maxLength) : next);
      }}
      fullWidth
      disabled={disabled}
      multiline
      minRows={minRows}
      inputRef={inputRef}
      // #1876 — #711 added select-on-focus here too ("same as ScoreInput,
      // for consistency"), which turned out to be unpopular with scribes:
      // tabbing into a comment field with existing remarks would select
      // the whole thing, so the next keystroke wiped it out instead of
      // editing it. Reverted; score fields (ScoreInput, common-fe) keep
      // select-on-focus, only the comment fields lose it.
      // #668 — Enter/Shift+Enter navigation. useSheetFieldNav's handler
      // preventDefaults every Enter, so this multiline field never gets a
      // literal newline inserted (#669 hardens the field's own paste/typing
      // path further; this is the base guard shared by every field).
      onKeyDown={onKeyDown}
      inputProps={{ maxLength }}
      helperText={
        // #181 — "x of N remaining" (2026-06-18) replaces
        // the prior "used / max" form so the scribe sees how many
        // characters are LEFT, not how many they've used.
        showCounter
          ? `${maxLength - v.length} of ${maxLength} remaining`
          : undefined
      }
      FormHelperTextProps={{
        sx: { textAlign: "right", m: 0, mt: 0.25 },
      }}
    />
  );
}

export default function ScoringForm() {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("score");
  // #188 — drives the Synced indicator below.
  const { online } = useScoreContext();
  // card #139 — the scribe's own sub, so UtilityBanner can gate the X on
  // "did I raise this" (server independently enforces originator-or-admin).
  const { user } = useAuth();
  const mySub = user?.profile?.sub ?? null;
  // card #139 — server-persisted, cross-scribe-polled BLOOD/LAMENESS flags
  // for this ride (replaces the old in-memory BroadcastContext mock).
  const {
    events: utilityEvents,
    raise: raiseUtilityEvent,
    clear: clearUtilityEvent,
  } = useRideUtilityEvents(rideId, { online });
  // Card #1992 — ride-level No Show (waterfalls to every panel judge via
  // polling, see useRideNoShow's own header comment).
  const rideNoShow = useRideNoShow(rideId);
  // Card #2064 — ride-level Excused (DR123.3's fourth term reason), same
  // waterfall pattern as No Show above (see useRideExcused's own header
  // comment).
  const rideExcused = useRideExcused(rideId);
  // #627 — the scribe's seat comes from the ride's class (scribeAssignments),
  // not the session. It keys the score sheet and filters the T/Q sections. A
  // scribe named at two seats of one class picks which one below.
  const {
    position: judgePosition,
    positions: assignedPositions,
    choosePosition,
    loading: positionLoading,
  } = useAssignedPosition(rideId);

  // #885 — no scribe seat on this class at all (as opposed to still
  // resolving, or a multi-seat class awaiting a choice). Distinguishing this
  // from "loading" matters: the final/not-final view-only gate below must
  // not flash before assignment actually resolves.
  const unassigned = !positionLoading && assignedPositions.length === 0;

  // #885 — the judge position an UNASSIGNED scribe is viewing read-only, once
  // the ride is confirmed FINAL (set by the gate below, from judgedPositions).
  // Feeding it into useScoreSheet AS IF it were the scored position means the
  // existing isCompleted-gated rendering (every mark input disabled, the
  // post-sign "Back to class" footer instead of Save/Confirm/Sign)
  // takes over automatically — a FINAL ride's sheets are already all
  // isCompleted, so no separate read-only mode/prop is needed anywhere below.
  const [viewPosition, setViewPosition] = useState(null);
  const effectivePosition = judgePosition ?? (unassigned ? viewPosition : null);

  const scoreApi = useScoreApi();
  // card #1664 — table cache preferred over the bundled catalog for the
  // test-name chip/header below (rideTestName); a hydration failure here
  // never affects `test` (assembled by useScoreSheet below), which is what
  // the sheet is actually scored against.
  const { catalogDefsById } = useCatalogDefs();
  // #885 — useScoreSheet deliberately REFUSES to fetch ride/class until its
  // judgeId is known ("nothing safe to load/save without a seat" — see its
  // own useEffect guard), so it never resolves for an unassigned viewer (who
  // has no seat until we know whether the ride is FINAL — a circular need).
  // Fetch ride+class independently, gated on `unassigned`, purely to decide
  // the gate below; once viewPosition is chosen, useScoreSheet's own
  // ride/klass take back over for everything else.
  const { data: gateRide, loading: gateRideLoading } = useApiResource(
    async () => (unassigned && rideId ? unwrap(await scoreApi.rides.getRide(rideId)) : null),
    [scoreApi, unassigned, rideId]
  );
  const { data: gateKlass, loading: gateKlassLoading } = useApiResource(
    async () =>
      unassigned && gateRide?.classId
        ? unwrap(await scoreApi.classes.getClass(gateRide.classId))
        : null,
    [scoreApi, unassigned, gateRide?.classId]
  );

  const {
    loading,
    error,
    ride,
    klass,
    test,
    horse,
    rider,
    sheet,
    isCompleted,
    isConfirmed,
    wasEverSigned,
    saveError,
    setMovementScore,
    setCollective,
    setSheetFields,
    setGeneralRemarks,
    setEntryTimeSeconds,
    applySheetWithReason,
    judgeName,
    complete,
    confirmMarks,
  } = useScoreSheet(rideId, effectivePosition);
  // #1878 — prefer the catalog's own clean name (name, stamped at reconcile
  // by catalogTestFields()) over FVD's raw testCode (apps/score-api/src/
  // handlers/daysheet.js:512-513 stamps the FVD day-sheet name onto testCode
  // instead); same order as RideList's manual-add picker. This is the
  // cold-start/offline fallback for rideTestName below when catalogDefsById
  // hasn't hydrated this ride's testId.
  const fallbackTestName = test ? test.name ?? test.testCode ?? null : null;
  // #2256 — DR203.14: In-Hand/Group/Championship classes say Handler, not
  // Rider. See isDshbHandlerClass's own doc comment (score-common's
  // dshb-class-kind.js) for the two reused signals this combines.
  // #2277 — override wins over the className heuristic here too.
  const isDshbHandlerSheet = useMemo(
    () =>
      isDshbHandlerClass({
        className: klass?.className,
        fvdTestId: test?.fvdId,
        test,
        dshbKindOverride: klass?.dshbClassKindOverride,
      }),
    [klass?.className, klass?.dshbClassKindOverride, test],
  );
  const resolvingPosition = positionLoading || (!judgePosition && assignedPositions.length > 1);

  // #761 — section-judge gating. Class.sectionJudgeAssignments (score-judge
  // roster ids, #759) says WHICH judge owns the technical/quality section;
  // resolving "is that judge sitting at MY position" requires the show's
  // judge roster (see ../util/sectionJudgeGating.js for why). Cache-first,
  // like every other lookup on this screen.
  const { data: judgeRoster } = useShowJudges(klass?.showId);
  const sectionGated = hasSectionJudgeAssignments(klass);
  const technicalOtherPosition = useMemo(
    () => resolvePositionForSection({ klass, judgeRoster, section: "technical" }),
    [klass, judgeRoster],
  );
  const qualityOtherPosition = useMemo(
    () => resolvePositionForSection({ klass, judgeRoster, section: "quality" }),
    [klass, judgeRoster],
  );
  // #761 AC2 — a locked section stays read-only, but shows the assigned
  // booth's real values once that booth has completed its own sheet (see
  // useOtherSectionSubmission: reads the same local cache useScoreSheet's
  // own fetch cycle already populates for every judge on this ride).
  const technicalOtherSheet = useOtherSectionSubmission(rideId, technicalOtherPosition, loading);
  const qualityOtherSheet = useOtherSectionSubmission(rideId, qualityOtherPosition, loading);
  const otherSheetForSection = (section) =>
    section === "technical" ? technicalOtherSheet : section === "quality" ? qualityOtherSheet : null;
  // #885 — effectivePosition (not the raw judgePosition, which is null for an
  // unassigned read-only viewer) so a viewed sheet renders the RIGHT
  // section/movements for the position being looked at, not this caller's
  // (nonexistent) own position. Equals judgePosition for an assigned scribe.
  const isMySectionEditable = (section) =>
    isSectionEditableForBooth({ klass, judgeRoster, position: effectivePosition, section });
  // #735 — bottom-of-sheet signature box. Only fetched once signed
  // (isCompleted); the show-wide per-judge signature (#731) is immutable at
  // that point, so this is a one-shot fetch, not polled.
  const { signature: sheetSignature, loading: sheetSignatureLoading } = useSheetSignature(
    sheet?.id,
    { enabled: Boolean(isCompleted) },
  );

  // The scribe scores for one judge position; the judge's name comes from the
  // class's judgeAssignments (FVD provides names, not Person ids).
  const judge = judgeName
    ? { id: effectivePosition, name: judgeName, firstName: judgeName, lastName: "" }
    : null;

  // Class running order, for the "Ride N of M" sequence label.
  const { data: classRides } = useApiResource(
    async () => (klass?.id ? unwrap(await scoreApi.rides.listRides(klass.id)) : []),
    [scoreApi, klass?.id]
  );

  // #885 — klass is null for an unassigned viewer until viewPosition is
  // chosen (useScoreSheet's own klass, per the comment above); gateKlass
  // (fetched independently, unconditionally of a seat) fills in until then.
  const klassForGate = klass ?? gateKlass;
  // #1991 AC1 — "panel judging exists" reuses the identical judge-topology
  // check scoreboard-mode-payload.js already uses ((count || 0) > 1): a
  // section-judge (two-judge T/Q split) class counts its assigned sections
  // via the same helper, so it correctly reads as panel too. Never a
  // locally-computed judge count.
  const isPanelClass = (expectedJudgeCountForClass(klassForGate) || 0) > 1;
  // #1989 — cache-first class-rides fetch, kept for rideStatus below (the
  // unassigned-viewer FINAL gate). #1989 removed the post-sign "Next ride in
  // class" button that used to be this hook's other consumer (unreliable,
  // reported not consistently landing on the next ride); "Back to class"
  // (below) plus picking a ride off the class running order is the surviving
  // path to the next ride.
  const { data: nextRideClassData, loading: classRidesLoading } = useRidesForClass(
    klassForGate?.id
  );

  // #885 — this ride's server-derived lifecycle status, read off the SAME
  // cache-first class-rides fetch above — no second source of truth. Only
  // consulted for an UNASSIGNED scribe (the final/not-final view-only gate
  // below); an assigned scribe's own sheet state already governs everything
  // they see. Matched on the URL's rideId (always known), not `ride?.id`
  // (null for an unassigned viewer pre-pick).
  const rideStatus = nextRideClassData?.rides?.find((r) => r.id === rideId)?.status ?? null;
  const rideIsFinal = rideStatus === DERIVED_RIDE_STATUSES.FINAL;

  // #885 — every position actually JUDGED in this class (not scribe-assigned)
  // — the read-only viewer's candidate list, since an unassigned scribe has
  // no seat of their own to default to.
  const judgedPositions = useMemo(() => judgedPositionsFor(klassForGate), [klassForGate]);

  // #885 — auto-pick the sole judged position once the ride is known FINAL; a
  // two-judge class instead offers a picker (see the gate below).
  useEffect(() => {
    if (unassigned && rideIsFinal && !viewPosition && judgedPositions.length === 1) {
      setViewPosition(judgedPositions[0]);
    }
  }, [unassigned, rideIsFinal, viewPosition, judgedPositions]);

  // #733 (judge-sig E) — show-wide judge signatures now live in scoreadmin
  // (#732). Before a sheet can be signed & completed, every judge assigned
  // to this class must have a signature on file — otherwise the show
  // secretary needs to go capture one first. `klass.judgeIds` (#730) is the
  // deduped set of judge-person ids for the class.
  const { data: showJudges } = useApiResource(
    async () => (klass?.showId ? unwrap(await scoreApi.judges.listJudges(klass.showId)) : []),
    [scoreApi, klass?.showId]
  );
  // #1824 — a judgeId in klass.judgeIds may be a COMPOSITE roster row: FVD
  // packs two real judges who both sat at C into one score-judge row (see
  // #1342, apps/score-api/src/lib/composite-judge.js). Nobody ever signs
  // that row itself, so checking ITS OWN signaturePresent below is always
  // false. Resolve every judgeId through #1342's one shared resolver (the
  // GET /judge/:id/resolve route) to find out — never by parsing the
  // composite row's run-together name.
  const { data: judgeCompositeById } = useApiResource(async () => {
    const ids = klass?.judgeIds ?? [];
    if (ids.length === 0) return new Map();
    const entries = await Promise.all(
      ids.map(async (id) => [id, unwrap(await scoreApi.judges.resolveJudgeComposite(id))])
    );
    return new Map(entries);
  }, [scoreApi, klass?.judgeIds]);
  const missingSignatureJudges = useMemo(() => {
    const judgeIds = klass?.judgeIds ?? [];
    if (judgeIds.length === 0 || !showJudges) return [];
    const byId = new Map(showJudges.map((j) => [j.id, j]));
    // Composite roster rows resolve to isComposite:true with primary/
    // secondary being the two REAL judge roster rows (already separate
    // entries in `showJudges`, so their own signaturePresent is real, not
    // the composite row's). While a resolution hasn't loaded yet, fall back
    // to the composite row's own (always-false) flag rather than falsely
    // clearing the gate — fails closed, same as the ordinary path.
    // Each entry carries the REAL judge's own id and roster name (never the
    // composite row's run-together name), so the blocking alert can say WHICH
    // judge the secretary still has to capture a signature for. `name` comes
    // off the roster row, never from splitting a composite name apart.
    const missing = [];
    for (const id of judgeIds) {
      const resolved = judgeCompositeById?.get(id);
      if (resolved?.isComposite) {
        for (const real of [resolved.primary, resolved.secondary]) {
          if (!real?.id || !byId.get(real.id)?.signaturePresent) {
            missing.push({ id: real?.id ?? id, name: byId.get(real?.id)?.name ?? real?.name ?? null });
          }
        }
      } else if (!byId.get(id)?.signaturePresent) {
        missing.push({ id, name: byId.get(id)?.name ?? null });
      }
    }
    return missing;
  }, [klass?.judgeIds, showJudges, judgeCompositeById]);
  const hasMissingSignature = missingSignatureJudges.length > 0;
  // #1824 (AC4): name the missing judge(s) in the blocking alert. Only when
  // EVERY missing judge resolved to a roster name; a judgeId that isn't in
  // showJudges at all (data drift) has no name to print, and a partial list
  // would read as though the unnamed one were already fine, so that case
  // falls back to #733's original unnamed wording. Two static i18n keys, one
  // per case, never a key name built at runtime.
  const missingSignatureJudgeNames = missingSignatureJudges.map((j) => j.name);
  const canNameMissingJudges =
    missingSignatureJudgeNames.length > 0 && missingSignatureJudgeNames.every(Boolean);

  // #1891 — resolveScoreSheetTotals (not calculateScoreSheet directly): once
  // this sheet's marks/errors have actually been confirmed under a Test def
  // that has SINCE been corrected (#1883/#1887/#1890 style catalog fixes),
  // this must show the sheet's own frozen percentage, matching every other
  // surface (report-defs, the USEF export, results.js), rather than
  // recomputing against `test` (a live catalog fetch that reflects the
  // correction, not what this sheet was actually scored against). A sheet
  // with no frozen maxPoints yet (still in progress) is unaffected — this
  // falls straight through to the same live math as before.
  const totals = useMemo(() => {
    if (!sheet || !test) return { totalPoints: 0, maxPoints: 0, percentage: 0 };
    return resolveScoreSheetTotals(sheet, test);
  }, [sheet, test]);

  const scoreByMovement = useMemo(() => {
    const m = new Map();
    for (const row of sheet?.movementScores ?? []) {
      m.set(row.movementNumber, row);
    }
    return m;
  }, [sheet]);

  // #542 — the movements this scribe/judge actually MUST score, per the
  // class's T/Q judging topology (position-based). #761 additionally
  // excludes any section Class.sectionJudgeAssignments (judge-identity
  // based) hands to a DIFFERENT judge, even if qualityJudging's topology
  // wasn't configured to already exclude it — so the sign-off completion
  // gate (movementFlags below) and Tab/Enter field order never require or
  // walk a section that isn't this booth's to fill in. This does NOT drive
  // the render loop any more (see renderMovements) — #761 needs the other
  // section to still be VISIBLE (read-only), just not required here.
  const visibleMovements = useMemo(() => {
    // #885 — effectivePosition, not judgePosition (see isMySectionEditable above).
    const base = visibleMovementsFor(test, klass, effectivePosition);
    if (!sectionGated) return base;
    return base.filter((m) => isMySectionEditable(m.section));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test, klass, effectivePosition, sectionGated, judgeRoster]);

  // #891 — each judge sees ONLY the section they score (technical judge at C
  // sees the technical movements, quality judge at B sees the quality
  // movements), never the other section. visibleMovementsFor already narrows
  // to this position's section for the two-judge young-horse topology, so
  // render exactly that. This supersedes #761's earlier choice to render BOTH
  // sections (the non-owned one read-only): for a section-split young-horse
  // test the other judge's rows are hidden, not shown greyed. Standard and
  // single-judge tests are unaffected — visibleMovementsFor returns the full
  // movement list when the class has no two-judge topology. Row-level
  // editability is still gated by isMySectionEditable inside the render loop.
  // #885 — effectivePosition, not judgePosition (see isMySectionEditable above).
  const renderMovements = useMemo(
    () => visibleMovementsFor(test, klass, effectivePosition),
    [test, klass, effectivePosition]
  );

  // #675 — gait sub-grouping is a freestyle-only concept (technical +
  // artistic). Young-horse/Children T/Q tests also use a "technical"
  // section marker but pair it with "quality", not "artistic" — gate on the
  // presence of an artistic-sectioned row rather than on a scoringModel
  // string so this doesn't misfire on that other technical section.
  const isFreestyleTest = useMemo(
    () => renderMovements.some((rm) => rm.section === "artistic"),
    [renderMovements]
  );

  // #1449 — def-level flag: "generalOnly" means this sheet has no per-
  // movement/per-collective comment cell at all (grid-shaped sheets like
  // the USEF Dressage Seat Equitation/Medal Additional Tests column, one
  // cell per rider row, not per movement — #1448). Absent/"perMovement"
  // (score-common's testSchema default) is every existing def's current
  // behaviour: both comment surfaces render, unchanged.
  const perMovementCommentsEnabled = test?.commentMode !== "generalOnly";

  // #668 — Tab AND Enter both walk score → comment → next score, in the
  // same movement-then-collective-then-General-Remarks order the sheet
  // renders in. rowOrder is left at the hook's mark-first default here —
  // #671 will thread a persisted footer setting through in its place.
  const sheetFieldRowIds = useMemo(
    () => [
      ...visibleMovements.map((m) => `movement-${m.movementNumber}`),
      ...(test?.collectives ?? []).map((c) => `collective-${c.movementNumber}`),
    ],
    [visibleMovements, test]
  );
  // #671 — the per-row keyboard sequence is a per-scribe preference, not a
  // fixed rule. Seeded from localStorage so it survives a reload and carries to
  // the next sheet and the next show; the footer switch below writes it back.
  const [fieldOrder, setFieldOrder] = useState(getSheetFieldOrder);

  const handleFieldOrderChange = (e) => {
    const next = e.target.checked ? COMMENT_FIRST : MARK_FIRST;
    setFieldOrder(next);
    setSheetFieldOrder(next);
  };

  const { registerField, handleKeyNav } = useSheetFieldNav({
    rowIds: sheetFieldRowIds,
    rowOrder: fieldOrder,
    // #1449 — keeps the per-row comment key out of the nav sequence
    // entirely when it isn't rendered, so Tab/Enter never dead-ends.
    commentsEnabled: perMovementCommentsEnabled,
  });

  // R1-UI-010 — running tally of earned vs possible points across only
  // the movements/collectives that have a score so far. Updates on every
  // keystroke because the source `sheet` is recreated each change.
  // #826 — the collectives loop mirrors the movements loop below so the
  // footer denominator reaches the test's full maxPoints (movements +
  // collectives, e.g. 330 + 70 = 400 for USEF Third Level Test 3) once
  // every box is scored, matching calculateScoreSheet's stored total.
  const running = useMemo(() => {
    let earned = 0;
    let possible = 0;
    for (const m of test?.movements ?? []) {
      const entry = scoreByMovement.get(m.movementNumber);
      if (entry && typeof entry.score === "number" && Number.isFinite(entry.score)) {
        const coef = m.coefficient ?? 1;
        // #221 — net out point deductions (absolute points, after coef),
        // clamped at 0, to match the per-row total box. #922 — percent-unit
        // deductions carry no `points` and hit the final percentage instead
        // (see runningPercentage), so they contribute 0 to the per-row net.
        const ded = (entry.deductions ?? []).reduce(
          (s, d) => s + (d.points ?? 0),
          0,
        );
        earned += Math.max(0, entry.score * coef - ded);
        possible += SCORE_MAX * coef;
      }
    }
    for (const c of test?.collectives ?? []) {
      const entry = sheet?.collectives?.[c.movementNumber];
      if (entry && typeof entry.score === "number" && Number.isFinite(entry.score)) {
        // #826 — collectives carry no deductions (no elimination /
        // movement-error menu on the collectives table, unlike movements).
        const coef = c.coefficient ?? 1;
        earned += entry.score * coef;
        possible += SCORE_MAX * coef;
      }
    }
    return { earned, possible };
  }, [test, scoreByMovement, sheet]);

  // R1-UI-024 / #187 / #767 — arena-entry timer state. A single cycling
  // control at the C position: IDLE ("Bell") -> a running window
  // ("In Ring", RUNNING/PAUSED sub-states, Pause+Restart available) ->
  // STOPPED (a "Ride in progress" pill, Pause+Restart gone for good).
  //   IDLE     → !bellStopped && bellRunningSince === null && accumulatedElapsed === 0
  //   RUNNING  → !bellStopped && bellRunningSince !== null (live elapsed = accumulated + segment)
  //   PAUSED   → !bellStopped && bellRunningSince === null && accumulatedElapsed > 0
  //   STOPPED  → bellStopped === true (elapsed frozen at accumulatedElapsed)
  // bellNow ticks at 50ms only while RUNNING so the chip stays current.
  // #768 (separate card) owns flipping persisted ride status off these
  // presses — not touched here.
  const [bellRunningSince, setBellRunningSince] = useState(null);
  const [accumulatedElapsed, setAccumulatedElapsed] = useState(0);
  const [bellNow, setBellNow] = useState(() => Date.now());
  const [bellStopped, setBellStopped] = useState(false);
  useEffect(() => {
    if (bellRunningSince === null) return undefined;
    const id = setInterval(() => setBellNow(Date.now()), 50);
    return () => clearInterval(id);
  }, [bellRunningSince]);

  // R1-UI-016 — autosave indicator state. The in-memory store updates
  // synchronously (R1-UI-015), so we synthesize a brief "Saving…" flash
  // so the user perceives the write.
  const [saveStatus, setSaveStatus] = useState("saved");
  const skipInitialSaveRef = useRef(true);
  useEffect(() => {
    if (!sheet) return;
    if (skipInitialSaveRef.current) {
      skipInitialSaveRef.current = false;
      return;
    }
    setSaveStatus("saving");
    const id = setTimeout(() => setSaveStatus("saved"), 400);
    return () => clearTimeout(id);
  }, [sheet]);

  // R1-UI-017 / #169 / #171 / #226 — sign-time gate. Each pending flag is
  // { key, section: "movement" | "collective", label, movementNumber,
  //   kind: "zero" | "missing" | "flagged" }. Zero and missing are
  // checkboxable (scribe affirms the cell as intentional); flagged rows
  // are NOT — the scribe has to Go back and clear the flag from the row
  // itself. Continue stays disabled while any flagged row is present
  // (movement OR collective), even if every zero/missing is ticked. `key`
  // disambiguates movement vs collective rows that share a number.
  const [pendingFlags, setPendingFlags] = useState([]);
  const [acknowledgedFlags, setAcknowledgedFlags] = useState(new Set());
  // #171 — final sign-confirmation modal. Opens when the resolve
  // dialog clears OR when handleConfirmScore found no issues at all.
  // finalize() only fires from this modal's Sign button so the
  // permanent action always has a "cannot be undone" gate.
  const [signConfirmOpen, setSignConfirmOpen] = useState(false);
  // #698 — Confirm Score modal: the FIRST stage's "are you sure" gate.
  // Opens once validation clears (same path signConfirmOpen used to open
  // from); Confirm Score in this dialog is the only thing that ever calls
  // confirmMarks() — a one-way, irreversible freeze (no un-confirm anywhere
  // in this file, by design).
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // #714 — confirmMarks() is a real network write (enqueue + flush); disable
  // the confirm control and show a spinner for the window it's in flight so
  // a scribe can't double-submit or navigate away mid-write.
  const [isConfirmingMarks, setIsConfirmingMarks] = useState(false);
  // #901 — reopened-after-sign restore: a sheet that was confirmed/signed
  // before being reopened for a correction shows a single "Save" button
  // instead of the two-stage Confirm Score / Complete & Sign footer.
  const [isSaving, setIsSaving] = useState(false);
  // #515 — Sign & Complete had no loading affordance at all: finalize()
  // fired complete() without awaiting it, so a slow/offline write gave zero
  // feedback and a double-tap could double-fire. Same isX/setIsX + try/
  // finally shape as isSaving/isConfirmingMarks above.
  const [isSigning, setIsSigning] = useState(false);

  // #733 (judge-sig E) — the per-test/per-sheet signature pad (#206/#485)
  // is gone. Judge signatures are now show-wide, captured once in
  // scoreadmin (judge-sig D, #732) and adopted at finalize time; see
  // `missingSignatureJudges` below for the submit-time gate.

  // #208 / #209 — per-row action menu (elimination reasons + point
  // deduction). A single shared Menu anchored to whichever row's icon
  // was clicked; `rowMenu.movementNumber` is the row in context.
  const [rowMenu, setRowMenu] = useState({ anchorEl: null, movementNumber: null });
  // #208 — custom-reason dialog when the scribe picks "Custom reason…".
  // movementNumber is captured so the custom elimination still attaches to
  // the row it was raised on (#221).
  const [customReason, setCustomReason] = useState({
    open: false,
    value: "",
    movementNumber: null,
  });
  // #209 — movement-level point-deduction dialog (reason + points),
  // distinct from elimination. movementNumber is the row in context.
  const [deduction, setDeduction] = useState({
    open: false,
    movementNumber: null,
    reason: "",
    points: "",
  });

  // #208 / #221 — sheet-level elimination is a LIST of reasons (each
  // individually removable via an X). Eliminated when the list is
  // non-empty.
  const eliminations = sheet?.eliminations ?? [];
  const eliminated = eliminations.length > 0;
  const closeRowMenu = () =>
    setRowMenu({ anchorEl: null, movementNumber: null });
  const eliminate = (reason, movementNumber = rowMenu.movementNumber) => {
    // #754 — Blood can only ever be indicated ONCE per box: guard against
    // a double-fire (fast double-click, or reopening the row menu and
    // picking it again) stacking a second Blood entry on the same
    // movement. Other reasons are unrestricted (a scribe may legitimately
    // want two "Errors of course" entries, etc).
    if (
      reason === "Blood" &&
      eliminations.some(
        (e) => e.reason === "Blood" && e.movementNumber === movementNumber
      )
    ) {
      closeRowMenu();
      return;
    }
    setSheetFields({
      eliminations: [
        ...eliminations,
        { id: makeId(), reason, movementNumber },
      ],
    });
    closeRowMenu();
  };
  // #266 — removing an elimination (or a point deduction, or an error of
  // course — see removeError/removeDeduction below) is gated on a
  // scribe-entered reason, forensically recorded (see removalReason state
  // + confirmRemoval below). These three functions only OPEN the dialog;
  // the actual removal happens once a non-empty reason is confirmed.
  const removeElimination = (id) => {
    // #754 — Blood is forensic: once recorded it can never be removed, no
    // matter what calls this. Nothing in the UI offers a remove action for
    // a Blood entry (see PenaltiesBanner / lineMarkers below), but the
    // invariant is enforced here too so it can't be bypassed.
    const target = eliminations.find((e) => e.id === id);
    if (target?.reason === "Blood") return;
    setRemovalReason({ open: true, kind: "elimination", id, movementNumber: null, reason: "" });
  };

  // #916 (part A) — Scratch / No Show gavel-menu items. Distinct from the
  // eliminate() above: these set the RIDE's status via score-api directly
  // (a cloud-only, admin-gated write — see setRideStatus), not a local sheet
  // field, so they get their own confirm step + loading/error state. Card
  // #1073 (separate) is what eventually writes the reason back to FVD.
  const [termReasonConfirm, setTermReasonConfirm] = useState({
    open: false,
    reason: null,
  });
  const [isSettingTermReason, setIsSettingTermReason] = useState(false);
  const [termReasonError, setTermReasonError] = useState(null);
  const openTermReasonConfirm = (reason) => {
    closeRowMenu();
    setTermReasonError(null);
    setTermReasonConfirm({ open: true, reason });
  };
  const closeTermReasonConfirm = () =>
    setTermReasonConfirm({ open: false, reason: null });
  const confirmTermReason = async () => {
    const reason = termReasonConfirm.reason;
    if (!reason || !rideId) return;
    try {
      setIsSettingTermReason(true);
      setTermReasonError(null);
      await scoreApi.rides.setRideStatus(rideId, {
        status: RIDE_STATUSES.SCRATCHED,
        termReason: reason,
      });
      closeTermReasonConfirm();
    } catch (err) {
      setTermReasonError(err);
    } finally {
      setIsSettingTermReason(false);
    }
  };

  // Card #1992 — ride-level "No Show" confirm (presiding judge, C, only —
  // #916 COLLISION: this is a DIFFERENT flow from the Scratch/No Show term
  // reason above. That one scratches the ride administratively via
  // setRideStatus and stays open to any position; this one is the C-only,
  // waterfalling, undo-able elimination via markRideNoShow/useRideNoShow.
  // Both coexist deliberately (settled + tested precedence: a stored
  // SCRATCHED wins outright over an active ride.noShow) — see the card
  // comment on #1992 for the full writeup. Menu labels are kept visibly
  // distinct (below) so a scribe under pressure can tell them apart.
  //
  // Cancel/onClose are NEVER gated on the in-flight write (unlike
  // closeTermReasonConfirm above) — a scribe on a live show floor must
  // always be able to back out, per the card.
  const [noShowConfirmOpen, setNoShowConfirmOpen] = useState(false);
  const openNoShowConfirm = () => {
    closeRowMenu();
    setNoShowConfirmOpen(true);
  };
  const closeNoShowConfirm = () => setNoShowConfirmOpen(false);
  const confirmNoShow = async () => {
    try {
      await rideNoShow.mark();
      setNoShowConfirmOpen(false);
    } catch {
      // rideNoShow.markError carries the failure; dialog stays open so the
      // scribe can retry or Cancel out.
    }
  };

  // Card #2064 — ride-level "Excused" confirm (DR123.3's fourth term reason,
  // presiding judge C or admin only). Same shape as the No Show confirm
  // just above: cancel/onClose are never gated on the in-flight write.
  const [excusedConfirmOpen, setExcusedConfirmOpen] = useState(false);
  const openExcusedConfirm = () => {
    closeRowMenu();
    setExcusedConfirmOpen(true);
  };
  const closeExcusedConfirm = () => setExcusedConfirmOpen(false);
  const confirmExcused = async () => {
    try {
      await rideExcused.mark();
      setExcusedConfirmOpen(false);
    } catch {
      // rideExcused.markError carries the failure; dialog stays open so the
      // scribe can retry or Cancel out.
    }
  };

  // #2080: one confirm dialog shared by every "clear annotations" button
  // (Test Overview items, movement description/directive, collective
  // name/directive). `clearAnnotationsTarget` holds { label, onConfirm }
  // for whichever field's clear button was tapped; null when closed. AC2:
  // a stray tap can't silently wipe a judge's remarks, so tapping the
  // button never clears on its own, it only opens this dialog (same shape
  // as the Mark Ride No Show / Scratch confirm dialogs above: Cancel is
  // never disabled, a scribe can always back out).
  const [clearAnnotationsTarget, setClearAnnotationsTarget] = useState(null);
  const requestClearAnnotations = (label, onConfirm) =>
    setClearAnnotationsTarget({ label, onConfirm });
  const closeClearAnnotationsConfirm = () => setClearAnnotationsTarget(null);
  const confirmClearAnnotations = () => {
    clearAnnotationsTarget?.onConfirm();
    setClearAnnotationsTarget(null);
  };

  // #221 / #683 — errors of course. The penalty for the Nth error comes
  // from the test's catalog errorRule (buildErrorTiers); a points tier
  // records a sheet-level point deduction, a percent tier records a
  // sheet-level percentage deduction (FEI). Per #682 (2026-07-15) an
  // eliminate tier does NOT auto-eliminate for MVP — see recordError.
  const errors = sheet?.errors ?? [];
  // #773 — the error-of-course tiers come from the ride's CLASS scheme
  // (klass.errorDeductionScheme, set by the admin in scoreadmin, #772) when
  // one is set; otherwise they fall back to the catalog test's errorRule.
  // reconcile never persists the scheme, so a class carries it only on an
  // explicit admin pick — an unset class correctly defers to the catalog
  // (which encodes the right points/percent unit per test).
  const errorTiers = resolveErrorTiers({
    errorDeductionScheme: klass?.errorDeductionScheme,
    test,
  });
  // Percent-scheme tests (FEI YH/Children) express EVERY deduction as a
  // percentage via the "Error of course (penalty)" control. The raw "Point
  // deduction" field subtracts absolute points, so a scribe typing a percent
  // there would silently mis-score — hide it entirely on percent schemes.
  const isPercentErrorScheme = errorTiers.some((t) => t.percent !== undefined);
  // #1306 — materiale (USDF DR134) has no memorized course to deviate from,
  // so its catalog test sets errorRule.unit "none" and resolveErrorTiers
  // returns an empty array (#1300). An empty schedule means the sheet's
  // test/scheme has NO error-of-course concept at all: offering the
  // "Record" menu item would let a scribe record a real (0-point) entry for
  // something that can't happen. This only hides the ADD affordance -
  // any error already recorded on the sheet (e.g. from before the scheme
  // was made error-exempt) still renders and stays removable via the
  // existing `errors.length > 0` gate below, untouched.
  const hasErrorConcept = errorTiers.length > 0;
  const errorPoints = errors.reduce((s, e) => s + (e.points ?? 0), 0);
  const errorPercent = errors.reduce((s, e) => s + (e.percent ?? 0), 0);
  const errorOccurrences = errors.length;
  const nextErrorTier = errorTierForOccurrence(
    errorTiers,
    errorOccurrences + 1,
  );
  const recordError = () => {
    const occurrence = errorOccurrences + 1;
    const tier = errorTierForOccurrence(errorTiers, occurrence) ?? {};
    const label = `${ordinal(occurrence)} error of course`;
    // #682 — MVP does NOT auto-eliminate from errors (2026-07-15).
    // A tier's `eliminate` flag is ignored here: we only record the fault
    // (points/percent). The scribe eliminates by hand via ELIMINATION_REASONS
    // if the rules call for it. Auto-elimination can be revisited post-MVP.
    if (tier.percent !== undefined) {
      setSheetFields({
        errors: [
          ...errors,
          {
            id: makeId(),
            label,
            percent: tier.percent,
            movementNumber: rowMenu.movementNumber,
          },
        ],
      });
    } else {
      setSheetFields({
        errors: [
          ...errors,
          {
            id: makeId(),
            label,
            points: tier.points ?? 0,
            movementNumber: rowMenu.movementNumber,
          },
        ],
      });
    }
    closeRowMenu();
  };
  // #752 — removing an error doesn't just drop it: every remaining error's
  // occurrence shifts down by one, so its tier (and therefore its point/
  // percent deduction and label) must be recomputed against the SAME
  // errorTiers schedule used to record them, not left at the stale value
  // from when it was recorded.
  const removeError = (id) =>
    setRemovalReason({ open: true, kind: "error", id, movementNumber: null, reason: "" });
  // Sheet total with error points netted out (clamped at 0). Percent
  // deductions (FEI) apply to the final percentage instead — see
  // runningPercentageText below — since they're not expressible as points.
  //
  // #1891 — once confirmed, this must be the SAME number as the
  // marks-confirmed banner above (`totals`, resolveScoreSheetTotals), not a
  // fresh live tally: a def corrected after this sheet was confirmed would
  // otherwise leave the banner showing the frozen percentage while this
  // sticky footer (rendered unconditionally, on screen through signing)
  // showed a different one recomputed against the live def — the exact
  // same-surface contradiction this card exists to prevent. `running`
  // (below) stays the live "only what's been scored so far" partial tally
  // for the pre-confirm case, where there's no frozen value to defer to and
  // a partial denominator is the whole point of a running score.
  const netEarned = isConfirmed
    ? (totals.totalPoints ?? 0)
    : Math.max(0, running.earned - errorPoints);

  // #922 — percent-unit movement deductions (recorded on a percent-scheme
  // test) come off the final percentage exactly like error-of-course percent
  // tiers, since they aren't expressible as raw points. Sum them across every
  // movement's deduction list (points-unit deductions carry no `percent`).
  const deductionPercent = (sheet?.movementScores ?? []).reduce(
    (s, ms) =>
      s + (ms.deductions ?? []).reduce((t, d) => t + (d.percent ?? 0), 0),
    0,
  );

  // #1891 — the footer's denominator, same gate as netEarned above: once
  // confirmed, defer to `totals.maxPoints` (resolveScoreSheetTotals) rather
  // than `running.possible` (a fresh live tally against `test`, which
  // disagrees with the frozen value the instant a def correction lands).
  const footerPossible = isConfirmed ? (totals.maxPoints ?? 0) : running.possible;

  // #674 — the header renders the running percentage as headline-scale type
  // with the "%" as a small, subdued unit beside it. formatPercentage stays
  // the single source of the number's formatting (3dp, or "—" when the value
  // isn't finite); we only peel the unit off so it can be styled down.
  // #683 — FEI-style percent error deductions come off the percentage here
  // (clamped at 0), since they're not expressible as raw points. #922 — the
  // same for percent-unit movement deductions (deductionPercent).
  // #1891 — once confirmed, `totals.percentage` (resolveScoreSheetTotals)
  // IS the percentage — it already nets errors/eliminations internally
  // (calculateScoreSheet) and, on a frozen-vs-live disagreement, is the
  // sheet's own already-stored value, matching the marks-confirmed banner
  // exactly. Re-deriving from netEarned/errorPercent/deductionPercent here
  // too would double-apply that netting and can disagree with the banner on
  // a frozen sheet — the bug this card exists to close.
  const runningPercentage = isConfirmed
    ? (totals.percentage ?? 0)
    : Math.max(
        0,
        (running.possible === 0 ? 0 : (netEarned / running.possible) * 100) -
          errorPercent -
          deductionPercent,
      );
  const runningPercentageText = formatPercentage(runningPercentage).replace(
    "%",
    "",
  );

  // #209 / #221 — point deductions are a LIST per movement (each
  // removable via an X). "Point deduction…" always adds a new one.
  const openDeduction = () => {
    setDeduction({
      open: true,
      movementNumber: rowMenu.movementNumber,
      reason: "",
      points: "",
    });
    closeRowMenu();
  };
  const closeDeduction = () =>
    setDeduction({ open: false, movementNumber: null, reason: "", points: "" });
  const applyDeduction = () => {
    const amount = Number(deduction.points);
    if (!deduction.reason.trim() || !Number.isFinite(amount) || amount <= 0)
      return;
    const mn = deduction.movementNumber;
    const existing =
      (sheet?.movementScores ?? []).find((m) => m.movementNumber === mn)
        ?.deductions ?? [];
    // #922 — the deduction's UNIT follows the class error-deduction scheme
    // (isPercentErrorScheme): a percent-scheme test records a percentage that
    // comes off the FINAL percentage (like an error-of-course percent tier),
    // a points-scheme test records raw points netted off the movement. Storing
    // the value in the matching field (percent vs points) is what keeps the
    // running math, chip, and any downstream reader from mixing units.
    const entry = isPercentErrorScheme
      ? { id: makeId(), reason: deduction.reason.trim(), percent: amount }
      : { id: makeId(), reason: deduction.reason.trim(), points: amount };
    setMovementScore(mn, {
      deductions: [...existing, entry],
    });
    closeDeduction();
  };
  const removeDeduction = (movementNumber, id) =>
    setRemovalReason({ open: true, kind: "deduction", id, movementNumber, reason: "" });

  // #266 — reason dialog gating removal of a point deduction / error of
  // course / (non-Blood) elimination. The reason rides applySheetWithReason
  // (useScoreSheet) as `changeReason`, which bypasses the debounce and PUTs
  // immediately so it lands in the forensic audit trail attached to THIS
  // removal, not some later unrelated save.
  const [removalReason, setRemovalReason] = useState({
    open: false,
    kind: null, // "elimination" | "error" | "deduction"
    id: null,
    movementNumber: null,
    reason: "",
  });
  const closeRemovalReason = () =>
    setRemovalReason({ open: false, kind: null, id: null, movementNumber: null, reason: "" });
  const confirmRemoval = async () => {
    const { kind, id, movementNumber, reason } = removalReason;
    const trimmed = reason.trim();
    if (!trimmed) return;
    if (kind === "elimination") {
      await applySheetWithReason(
        (s) => ({
          ...s,
          eliminations: (s.eliminations ?? []).filter((e) => e.id !== id),
        }),
        trimmed,
      );
    } else if (kind === "error") {
      await applySheetWithReason(
        (s) => ({
          ...s,
          // #752 — removing an error re-tiers the remaining ones against the
          // same errorTiers schedule they were recorded with.
          errors: retierErrors(
            (s.errors ?? []).filter((e) => e.id !== id),
            errorTiers,
          ),
        }),
        trimmed,
      );
    } else if (kind === "deduction") {
      await applySheetWithReason((s) => {
        const movementScores = s.movementScores ?? [];
        const idx = movementScores.findIndex((m) => m.movementNumber === movementNumber);
        const existing = idx >= 0 ? movementScores[idx].deductions ?? [] : [];
        const nextDeductions = existing.filter((d) => d.id !== id);
        const nextMovementScores =
          idx >= 0
            ? movementScores.map((m, i) =>
                i === idx ? { ...m, deductions: nextDeductions } : m,
              )
            : [...movementScores, { movementNumber, deductions: nextDeductions }];
        return { ...s, movementScores: nextMovementScores };
      }, trimmed);
    }
    closeRemovalReason();
  };

  // Non-hook derived values + handlers — safe to live after the hooks.
  // #2188 — DSHB in-hand/group/handler sheets have no test to ride: no time
  // limit and no error of course to signal (USEF DR210/DR211), so the bell/
  // arena-entry timer chrome is not merely unused, it's misleading on a
  // live sheet. score-api's catalog-enrich.js stamps `hasRideTiming: false`
  // on exactly that def family; every other def (ridden or not) leaves it
  // undefined, so testSchema's default (true, score-common validation.js)
  // applies and this reads `!== false` rather than a bare truthy check.
  const showRideTimer = test?.hasRideTiming !== false;
  const entryLimitSeconds = klass?.faultTimeLimitSeconds ?? 45;
  const isRunning = !bellStopped && bellRunningSince !== null;
  const isPaused = !bellStopped && !isRunning && accumulatedElapsed > 0;
  const liveSegmentSeconds = isRunning
    ? (bellNow - bellRunningSince) / 1000
    : 0;
  const bellElapsedSeconds =
    isRunning || isPaused || bellStopped
      ? accumulatedElapsed + liveSegmentSeconds
      : null;
  // #768 — best-effort background PATCH of the SHARED/persisted ride status
  // off a Bell/In-the-Ring/Restart press. Deliberately fire-and-forget: the
  // timer state above (#767) is fully local and already reflects the press
  // regardless of network, and scribe-fe kiosks run offline (card doctrine —
  // no escape hatches, no error dialogs the operator must dismiss), so a
  // failed/offline write here must never surface anything or block the
  // button. This is the ONLY thing #768 adds to this file; every timer state
  // transition above/below is #767's, unchanged.
  const setSharedRideStatus = (status) => {
    if (!rideId) return;
    scoreApi.rides.setRideStatus(rideId, { status }).catch(() => {});
  };
  // #767 — the main button now only moves the cycle FORWARD: idle -> "Bell"
  // starts the timer and relabels to "In Ring"; "In Ring" (pressed either
  // while running or paused — Pause no longer changes this button's label)
  // stops the timer for good and swaps the button for the "Ride in
  // progress" pill. Pausing/resuming mid-window is handleTogglePause below.
  const handleBell = () => {
    if (isRunning || isPaused) {
      // "In Ring" — commit the final elapsed time and stop for good.
      let total = accumulatedElapsed;
      if (isRunning) {
        const segment = (Date.now() - bellRunningSince) / 1000;
        total = accumulatedElapsed + segment;
        setAccumulatedElapsed(total);
        setBellRunningSince(null);
      }
      setEntryTimeSeconds(Math.round(total * 10) / 10);
      setBellStopped(true);
      // TODO(#198) — forensic log: InRing(elapsed=total, wallTs=Date.now())
      setSharedRideStatus(RIDE_STATUSES.IN_PROGRESS); // #768
    } else {
      // "Bell" — start the timer.
      const now = Date.now();
      setBellRunningSince(now);
      setBellNow(now);
      // TODO(#198) — forensic log: Bell(prevAccum=accumulatedElapsed, wallTs=now)
      setSharedRideStatus(RIDE_STATUSES.ENTERING_RING); // #768
    }
  };
  // #767 — Pause/Resume now lives on its own control, separate from the
  // main Bell/In Ring button; only actionable inside the running window.
  const handleTogglePause = () => {
    if (isRunning) {
      const segment = (Date.now() - bellRunningSince) / 1000;
      const total = accumulatedElapsed + segment;
      setAccumulatedElapsed(total);
      setBellRunningSince(null);
      setEntryTimeSeconds(Math.round(total * 10) / 10);
      // TODO(#198) — forensic log: Pause(elapsed=total, wallTs=Date.now())
    } else if (isPaused) {
      const now = Date.now();
      setBellRunningSince(now);
      setBellNow(now);
      // TODO(#198) — forensic log: Resume(prevAccum=accumulatedElapsed, wallTs=now)
    }
  };
  const handleResetBell = () => {
    setAccumulatedElapsed(0);
    setBellRunningSince(null);
    setEntryTimeSeconds(undefined);
    setBellStopped(false);
    // TODO(#198) — forensic log: Reset(priorAccum=accumulatedElapsed, wallTs=Date.now())
    setSharedRideStatus(RIDE_STATUSES.PENDING); // #768
  };

  // Bail-out nav lives in the session strip as clickable chips so the
  // chevron PageBreadcrumbs can go away. Clicking a crumb just
  // navigates; in-progress `sheet` state lives in useScoreSheet and is
  // dropped on unmount, which is the expected demo behavior.
  const crumbs = [
    {
      label: t("classes.title"),
      to: "/classes",
      onClick: () => navigate("/classes"),
    },
    {
      // #927 — show the class NUMBER alongside its name so the scribe can
      // confirm which class they're on, matching the "N. Name" convention
      // already used by ClassList/ClassSearchModal (classNumberLabel).
      label: klass ? classNumberLabel(klass) : "…",
      to: klass ? `/classes/${klass.id}` : undefined,
      onClick: klass ? () => navigate(`/classes/${klass.id}`) : undefined,
    },
    {
      label: ride ? t("rides.ridePrefix", { number: rideCnoNumber(ride) }) : "…",
    },
  ];

  // #260 — the ride's running-order position within its class ("Ride 2
  // of 10"), shown after the judge so it isn't mistaken for the CNO.
  // Based on the scheduled rides; emergency manual adds (not in the
  // scheduled list) don't get a sequence.
  const sequenceLabel = (() => {
    if (!ride || !klass) return null;
    const idx = (classRides ?? []).findIndex((r) => r.id === ride.id);
    if (idx < 0) return null;
    return t("rides.sequence", {
      position: idx + 1,
      total: (classRides ?? []).length,
    });
  })();

  // #627 — a scribe named at two seats of the same class (e.g. the C and E
  // judges of a two-judge young-horse class) has to say which seat they're in;
  // everything downstream — which sheet, which movements — hangs off it. The
  // choice is remembered per class, so this is asked once per class, not per ride.
  if (!positionLoading && !judgePosition && assignedPositions.length > 1) {
    return (
      <AppShell sessionNav={crumbs} classId={klass?.id ?? null}>
        <Stack spacing={2} alignItems="flex-start" sx={{ py: 4 }}>
          <Typography variant="h6" fontWeight={700}>
            {t("scoring.whichSeat")}
          </Typography>
          <Stack direction="row" spacing={1}>
            {assignedPositions.map((p) => (
              <Button
                key={p}
                variant="contained"
                size="large"
                data-testid={`seat-option-${p}`}
                onClick={() => choosePosition(p)}
              >
                {p}
              </Button>
            ))}
          </Stack>
        </Stack>
      </AppShell>
    );
  }

  // #885 — a scribe with no seat on this class (unassigned) may still VIEW a
  // FINAL ride's scoresheet(s) read-only; only a NOT-final ride blocks them,
  // and with a message that says so (scoring.notFinalYet) instead of the old
  // generic "aren't assigned" wording (retired — every unassigned case now
  // resolves to either the read-only view or this not-final block). Ride/
  // class-rides data must have loaded before deciding which screen to show,
  // or this would flash the wrong one on first paint.
  if (unassigned) {
    if (gateRideLoading || gateKlassLoading || classRidesLoading) {
      return (
        <AppShell sessionNav={crumbs} classId={klassForGate?.id ?? null}>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: colors.foxOrange }} />
          </Box>
        </AppShell>
      );
    }

    if (!rideIsFinal || judgedPositions.length === 0) {
      return (
        <AppShell sessionNav={crumbs} classId={klassForGate?.id ?? null}>
          <Typography color="error">{t("scoring.notFinalYet")}</Typography>
          <Button onClick={() => navigate("/classes")} sx={{ mt: 2 }}>
            Back to classes
          </Button>
        </AppShell>
      );
    }

    if (!viewPosition) {
      // #885 — a two-judge class has no single "my seat" to default to; let
      // the viewer pick which judge's sheet to look at (same pattern as the
      // assigned multi-seat chooser above).
      if (judgedPositions.length > 1) {
        return (
          <AppShell sessionNav={crumbs} classId={klassForGate?.id ?? null}>
            <Stack spacing={2} alignItems="flex-start" sx={{ py: 4 }}>
              <Typography variant="h6" fontWeight={700}>
                {t("scoring.whichSheetToView")}
              </Typography>
              <Stack direction="row" spacing={1}>
                {judgedPositions.map((p) => (
                  <Button
                    key={p}
                    variant="contained"
                    size="large"
                    data-testid={`view-option-${p}`}
                    onClick={() => setViewPosition(p)}
                  >
                    {p}
                  </Button>
                ))}
              </Stack>
            </Stack>
          </AppShell>
        );
      }
      // Sole judged position: the auto-pick effect above sets viewPosition
      // on the next render (it can't happen synchronously mid-render) —
      // show the spinner for that one extra frame rather than falling
      // through with no position at all.
      return (
        <AppShell sessionNav={crumbs} classId={klassForGate?.id ?? null}>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: colors.foxOrange }} />
          </Box>
        </AppShell>
      );
    }
    // viewPosition is set: fall through to the normal render below, now
    // driven by effectivePosition (== viewPosition) — a FINAL ride's sheet
    // is already isCompleted, so the existing isCompleted-gated rendering
    // disables every mark input and swaps Confirm/Sign for "Back to class",
    // with no separate read-only mode needed.
  }

  if (loading || resolvingPosition) {
    // #714 — the scoring form doesn't render at all until the sheet has
    // loaded, so there's nothing to "disable": this whole-page spinner IS
    // the disabled state. sx-forced to the brand accent since primary.main
    // (dark grey) is invisible on a dark background.
    return (
      <AppShell sessionNav={crumbs} classId={klass?.id ?? null}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: colors.foxOrange }} />
        </Box>
      </AppShell>
    );
  }

  if (error || !ride || !test) {
    // #462 — the ride loaded but its test didn't resolve (missing/unmapped
    // ride.testId). We deliberately no longer substitute the class test, so
    // flag it loud for review instead of scoring against the wrong test.
    const testUnresolved = !error && ride && !test;
    return (
      <AppShell sessionNav={crumbs} classId={klass?.id ?? null}>
        <Typography color="error">
          {testUnresolved
            ? t("scoring.noTestAssigned")
            : error?.message ?? "Ride or test not found."}
        </Typography>
        <Button onClick={() => navigate("/classes")} sx={{ mt: 2 }}>
          Back to classes
        </Button>
      </AppShell>
    );
  }

  // #663 — no more auto-navigate back to the class list on sign: real-scribe
  // feedback was that bouncing straight off the sheet is jarring right after
  // the signature ceremony. The post-sign footer below offers "Back to
  // class", and the scribe picks the next ride off the class running order.
  // #1989 removed the sibling "Next ride in class" button (reported not
  // reliably landing on the next ride) — "Back to class" -> RideList
  // (App.jsx's /classes/:classId route) is the one surviving path back into
  // scoring.
  // #515 — now awaited (was fire-and-forget): the Sign button drives its
  // spinner off this promise, which resolves on complete()'s local commit
  // (see useScoreSheet) — an offline/optimistic sign doesn't hang waiting on
  // the network.
  const finalize = () => complete();

  // #663 — explicit "Back to class" action (used to be finalize()'s implicit
  // navigate).
  const handleBackToClass = () => {
    if (klass?.id) navigate(`/classes/${klass.id}`);
  };

  // #698 — Confirm Score: the irreversible dialog's own Confirm button calls
  // this, never the marks themselves. No un-confirm route exists anywhere.
  const handleConfirmMarks = async () => {
    setConfirmDialogOpen(false);
    setIsConfirmingMarks(true);
    try {
      await confirmMarks();
    } finally {
      setIsConfirmingMarks(false);
    }
  };

  // #733 (judge-sig E) — no per-sheet signature to gather anymore; Sign &
  // Complete is already blocked (see `hasMissingSignature` below) while any
  // class judge lacks a show-wide signature, so by the time this fires
  // finalize() can simply adopt the stored signature on record.
  // #515 — was a fire-and-forget void call with no loading affordance and no
  // error handling; now mirrors handleSave/handleConfirmMarks's isX + try/
  // finally shape so the Sign button shows a spinner and re-enables on error.
  const handleSign = async () => {
    setSignConfirmOpen(false);
    setIsSigning(true);
    try {
      await finalize();
    } finally {
      setIsSigning(false);
    }
  };

  // #901 — reopened-after-sign restore. This sheet was already confirmed and
  // signed once (wasEverSigned) before an admin reopened it for a small
  // correction; the judge already committed to signing it, so a single tap
  // silently reruns the freeze — no "Confirm Score?" or "Sign sheet?" modal.
  // completeScoreSheet has no precondition on marksConfirmedAt and reuses the
  // judge's existing show-wide signature via offloadSignatureOnComplete
  // (same mechanism scoreadmin-fe's RideEditor.handleCompleteAndSign relies
  // on), so calling it alone restores CONFIRMED (isConfirmed derives from
  // completedAt too, #696 back-compat).
  // #663 — same no-auto-nav rule as finalize(): re-signing a reopened sheet
  // lands on the same post-sign Back to class footer.
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await complete();
    } finally {
      setIsSaving(false);
    }
  };

  // #698 — renamed from handleComplete: this validation now gates CONFIRM
  // (stage 1), not Complete/Sign (stage 2). Sign & Complete no longer
  // re-runs it — it's disabled until isConfirmed, so by the time it's
  // clickable this has already passed.
  const handleConfirmScore = () => {
    // #208 — an eliminated sheet bypasses the mandatory-field
    // validation entirely; go straight to the confirmation modal
    // (relabeled "Confirm elimination").
    if (eliminated) {
      setConfirmDialogOpen(true);
      return;
    }
    // #169 / #171 — gate covers three states:
    //   zero    — score === 0 (acknowledgeable)
    //   missing — no score entered (acknowledgeable)
    //   flagged — m.flagged === true (NOT acknowledgeable; must
    //             unflag the row in the sheet to proceed)
    // Walk test.movements so missing rows surface even when the
    // sheet has no entry for them at all.
    const rowByNum = new Map(
      (sheet?.movementScores ?? []).map((m) => [m.movementNumber, m]),
    );
    const movementFlags = visibleMovements
      .map((mv) => {
        const row = rowByNum.get(mv.movementNumber);
        const base = {
          key: `m${mv.movementNumber}`,
          section: "movement",
          label: `Movement ${mv.movementNumber}`,
          movementNumber: mv.movementNumber,
        };
        if (row?.flagged === true) return { ...base, kind: "flagged" };
        const score = row?.score;
        if (score === 0) return { ...base, kind: "zero" };
        if (typeof score !== "number" || !Number.isFinite(score)) {
          return { ...base, kind: "missing" };
        }
        return null;
      })
      .filter(Boolean);
    // #226 — a flagged collective blocks signing exactly like a flagged
    // movement: not acknowledgeable, the scribe must clear it on the row.
    // (Collective zero/missing scores are not gated here.)
    const collectiveFlags = (test?.collectives ?? [])
      .filter(
        (c) => sheet?.collectives?.[c.movementNumber]?.flagged === true,
      )
      .map((c) => ({
        key: `c${c.movementNumber}`,
        section: "collective",
        label: `Collective “${c.name}”`,
        movementNumber: c.movementNumber,
        kind: "flagged",
      }));
    const flags = [...movementFlags, ...collectiveFlags];
    if (flags.length > 0) {
      setPendingFlags(flags);
      setAcknowledgedFlags(new Set());
      return;
    }
    // No issues — jump straight to the Confirm Score modal (stage 1).
    setConfirmDialogOpen(true);
  };

  const flaggedCount = pendingFlags.filter((f) => f.kind === "flagged").length;
  const ackableFlags = pendingFlags.filter((f) => f.kind !== "flagged");
  const allAckableAcknowledged =
    ackableFlags.length === 0 ||
    ackableFlags.every((f) => acknowledgedFlags.has(f.key));
  const canProceedFromResolve = flaggedCount === 0 && allAckableAcknowledged;

  return (
    <AppShell
      sessionNav={crumbs}
      sessionJudge={judge}
      sessionPosition={effectivePosition}
      sessionSequence={sequenceLabel}
      classId={klass?.id ?? null}
    >
      <Stack
        // #721 — measured by useSheetFieldNav so Tab/Enter scrolls a
        // focused field out from behind this sticky bar.
        data-testid="scoring-subheader"
        // #163 — at narrow widths the left + right header groups stack
        // vertically (left on top, right below) rather than letting chips
        // wrap inside either group.
        // #173 / #177 — pin the row flush under the SessionHeader.
        // Negative mt absorbs AppShell's inner Box top padding so the
        // header sits right at SessionHeader's bottom edge both at
        // rest AND when sticky (no scrolling gap). Negative mx +
        // re-applied px stretches the bar edge-to-edge to match the
        // sticky footer (#174). #1665 — this used to hardcode top:105
        // (AppBar 64 + SessionHeader py 16 + one chip row 24 + borderBottom 1),
        // which assumed SessionHeader was always exactly one chip row tall.
        // The #896 testName chip (this describe's own subject) can push
        // SessionHeader to wrap onto a second row, and a stale 105px then sat
        // ABOVE SessionHeader's real bottom edge, covering this row's own
        // CNO/Rider/Horse content (score-page-ui.cy.js, class-fault-time-
        // limit.cy.js — both real regressions, proven by neutering the
        // testName fallback call site and re-running). AppShell now measures
        // SessionHeader's live height (ResizeObserver) and publishes it as
        // --fv-session-header-bottom; 105px stays only as the var() fallback
        // for the one frame before that effect runs. Opaque bg + bottom
        // divider so movement rows don't bleed through.
        // #1701 — that #896 testName chip is gone now (SessionHeader no
        // longer renders one at all, dropped as the duplicate half of the
        // test-name-shown-twice fix), but the measurement mechanism stays:
        // judge/sequence chips can still make SessionHeader wrap at narrow
        // widths (mitigated, not eliminated, by their own `md`-breakpoint
        // collapse below), and a long breadcrumb trail always could.
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        // #681 — the enlarged readout is wide enough that at tablet-portrait
        // width (768) the left group used to run underneath the control
        // cluster. Let the two groups wrap instead of overlapping (useFlexGap
        // so the wrapped row keeps its gap — Stack's default margin spacing
        // does not).
        useFlexGap
        flexWrap="wrap"
        sx={{
          position: "sticky",
          top: "var(--fv-session-header-bottom, 105px)",
          zIndex: 1100,
          bgcolor: "background.default",
          mt: { xs: -2, sm: -4 },
          mx: { xs: -2, sm: -4 },
          px: { xs: 2, sm: 4 },
          py: 1,
          mb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {/* Left: CNO / Rider / Horse, in that order (#725 — promoted here
            now that the running score moved to the sticky footer; #681's
            oversized headline readout and the right-shoved identity pills
            are both reverted). Prominent but not huge. */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexGrow: 1, flexWrap: "wrap", minWidth: 0, alignItems: "center" }}
        >
          {isCompleted && (
            <Chip label="Completed" size="small" color="success" />
          )}
          {eliminated && (
            <Chip
              label="Eliminated"
              size="small"
              color="error"
              sx={{ fontWeight: 700 }}
            />
          )}
          <Typography
            component="span"
            data-testid="scoring-cno"
            sx={{
              fontWeight: 700,
              lineHeight: 1,
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              color: "text.primary",
            }}
          >
            {ride ? `#${rideCnoNumber(ride)}` : "…"}
          </Typography>
          {rider && (
            <Chip
              // #2256 — DR203.14: two explicit static i18n keys, branched on
              // isDshbHandlerSheet, never a dynamically-built key (repo
              // CLAUDE.md's i18n static-literal rule).
              label={
                isDshbHandlerSheet
                  ? t("scoring.handlerChipLabel", {
                      name: `${rider.firstName} ${rider.lastName}`,
                    })
                  : t("scoring.riderChipLabel", {
                      name: `${rider.firstName} ${rider.lastName}`,
                    })
              }
              size="small"
              variant="outlined"
            />
          )}
          <HorseChip horse={horse} />
          {/* #915 / #1664 — resolved per-ride off ride.testId, table cache
              preferred; ToC classes can carry a different test per ride.
              #1665 — falls back to fallbackTestName (the same `tests`
              IndexedDB-backed row useScoreSheet.js already loaded for
              scoring) rather than the retired score-common/catalog bundle.
              #1701 — this is now the ONLY test-name chip on the ride screen;
              SessionHeader's duplicate copy (breadcrumb row, #896) is gone. */}
          {ride && rideTestName(ride, catalogDefsById, fallbackTestName) && (
            <Chip
              data-testid="ride-test-name"
              label={`Test: ${rideTestName(ride, catalogDefsById, fallbackTestName)}`}
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
        {/* #164 — right side: [Start] (Time) (Saved) (Synced) [Utility].
            #725 — Horse/Rider identity pills reverted off this cluster;
            CNO/Rider/Horse now live in the left group instead (#681's
            right-shove undone). The outer flex on the parent Stack still
            drops this whole group under the left group at xs breakpoints
            (#163). */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexShrink: 0, flexWrap: "wrap" }}
        >
            {/* #2188 — no test to ride on a DSHB in-hand/group/handler
              sheet, so no bell and no ride-time countdown; see
              showRideTimer above. Everything inside this guard (bell/pill/
              pause/restart + the elapsed-time chip) is entirely absent from
              the DOM on an in-hand sheet, not just visually hidden. */}
          {showRideTimer && (
            <>
            {/* R1-UI-024 / #163 / #187 / #767 — entry-timer controls. A
              single cycling control at the C position: "Bell" (idle) ->
              "In Ring" (running window — Pause + Restart appear alongside
              it) -> a "Ride in progress" pill (Pause + Restart gone for
              good). Pause pauses/resumes without leaving the running
              window; Restart zeroes the timer and returns to "Bell". */}
          {bellStopped ? (
            <Chip
              data-testid="bell-ride-in-progress-pill"
              label={t("scoring.bellRideInProgress")}
              size="small"
              color="info"
              sx={{ minWidth: 84, fontWeight: 600 }}
            />
          ) : (
            <>
              <Button
                data-testid="bell-button"
                size="small"
                variant={isRunning ? "contained" : "outlined"}
                color={isRunning ? "warning" : "primary"}
                disabled={isCompleted}
                onClick={handleBell}
                startIcon={!isRunning ? <NotificationsActiveIcon /> : null}
                sx={{
                  minWidth: 84,
                  // #165 — outlined primary renders dim primary.main text
                  // against the dark background; force the non-running
                  // states to theme text color. The running state (filled
                  // warning) is unaffected.
                  ...(!isRunning && {
                    color: "text.primary",
                    "& .MuiButton-startIcon": { color: "text.primary" },
                  }),
                }}
              >
                {isRunning || isPaused
                  ? t("scoring.bellInRing")
                  : t("scoring.bellStart")}
              </Button>
              {/* #767 AC3 — Pause + Restart exist ONLY inside the running
                  window (after Bell, before In Ring); not present in the
                  idle state, gone for good once stopped. */}
              {(isRunning || isPaused) && (
                <>
                  <Tooltip
                    title={isRunning ? t("scoring.bellPause") : t("scoring.bellResume")}
                    arrow
                  >
                    <span>
                      <IconButton
                        data-testid="bell-pause-button"
                        size="small"
                        disabled={isCompleted}
                        onClick={handleTogglePause}
                        aria-label={
                          isRunning ? t("scoring.bellPause") : t("scoring.bellResume")
                        }
                        sx={{ p: 0.25, color: "text.primary" }}
                      >
                        {isRunning ? (
                          <PauseIcon fontSize="small" />
                        ) : (
                          <PlayArrowIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("scoring.bellRestart")} arrow>
                    <span>
                      <IconButton
                        data-testid="bell-restart-button"
                        size="small"
                        disabled={isCompleted}
                        onClick={handleResetBell}
                        aria-label={t("scoring.bellRestart")}
                        sx={{
                          p: 0.25,
                          // Same contrast treatment — `disabled` state
                          // already dims via MUI defaults; the enabled
                          // icon needs to read clearly against the dark
                          // page.
                          color: "text.primary",
                        }}
                      >
                        <RestartAltIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </>
          )}
          {(() => {
            // R1-UI-025 — attention state once elapsed > entryLimitSeconds.
            // #570 — the per-class fault time limit (faultTimeLimitSeconds)
            // drives this; once a ride runs over, keep it flagged for review
            // even after the bell is paused (fall back to the recorded
            // entryTimeSeconds, not just the live tick). Flag only — no
            // automatic penalty is applied for going over time.
            // #728 — pressing Bell suppresses the red: it must not trip
            // purely because wall-clock time passed the (often-wrong,
            // hardcoded-45s-default) limit while the scribe is actively
            // timing the ride this session. Only flag when the elapsed
            // value is stale/inherited data (sheet.entryTimeSeconds) and
            // the bell was never started this session. #767 — also true
            // once stopped (bellStopped): a completed timing session is
            // real recorded data, not stale, and must not blink.
            const hasStarted = isRunning || isPaused || bellStopped;
            const effectiveElapsed =
              bellElapsedSeconds ?? sheet?.entryTimeSeconds ?? null;
            const isExpired =
              !hasStarted &&
              effectiveElapsed !== null &&
              effectiveElapsed > entryLimitSeconds;
            // #166 / #167 / #168 / #186 — stopwatch-style mm:ss.xx.
            // Empty state reads 00:00.00 so the chip always looks like
            // a clock at rest. Two fractional digits make the live
            // timer visibly tick at the 50ms interval. #186 — also
            // show the total time inherited from the class
            // (entryLimitSeconds) as "elapsed / total" so the scribe
            // can see how much window is left.
            const fmt = (s) => {
              const m = Math.floor(s / 60);
              const r = s - m * 60;
              return `${String(m).padStart(2, "0")}:${r
                .toFixed(2)
                .padStart(5, "0")}`;
            };
            const elapsed =
              bellElapsedSeconds ?? sheet?.entryTimeSeconds ?? 0;
            const label = `${fmt(elapsed)} / ${fmt(entryLimitSeconds)}`;
            return (
              <Chip
                data-testid="entry-timer-chip"
                label={label}
                size="small"
                color={isExpired ? "error" : "default"}
                variant={isExpired ? "filled" : "outlined"}
                sx={{
                  fontFamily: "monospace",
                  // #186 — fits "00:00.00 / 00:45.00" (~19 chars).
                  minWidth: 168,
                  ...(isExpired && {
                    animation: "entryTimerBlink 1.6s ease-in-out infinite",
                    "@keyframes entryTimerBlink": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.55 },
                    },
                  }),
                }}
              />
            );
          })()}
            </>
          )}
          {/* Saved + Synced status dots (#162). */}
          <Tooltip
            title={saveStatus === "saving" ? "Saving…" : "Saved"}
            arrow
          >
            <CircleIcon
              aria-label={saveStatus === "saving" ? "Saving" : "Saved"}
              sx={{
                fontSize: 14,
                color:
                  saveStatus === "saving" ? "warning.main" : "success.main",
              }}
            />
          </Tooltip>
          {/* #188 — drive sync state from navigator.onLine via
              ScoreContext. Real server-sync wiring (R1-PLAN-010
              et al.) will refine this further. */}
          <Tooltip title={online ? "Synced" : "Offline"} arrow>
            {online ? (
              <CloudDoneIcon
                aria-label="Synced"
                sx={{ fontSize: 16, color: "success.main" }}
              />
            ) : (
              <CloudOffIcon
                aria-label="Offline"
                sx={{ fontSize: 16, color: "warning.main" }}
              />
            )}
          </Tooltip>
          <UtilityTrigger
            rideId={rideId}
            disabled={isCompleted}
            onRaise={raiseUtilityEvent}
            isPanel={isPanelClass}
          />
        </Stack>
      </Stack>
      {/* R1-UI-023 — end-of-test banner. Renders only once every
          ScoreSheet for this Ride is complete (Ride.allSheetsCompletedAt
          set by R1-PLAN-011); expanded composite line appears once the
          ride is admin-confirmed. */}
      {ride?.allSheetsCompletedAt && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: ride?.adminConfirmedAt ? "success.lightest" : "info.lightest",
            border: 1,
            borderColor: ride?.adminConfirmedAt ? "success.main" : "info.main",
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {ride?.adminConfirmedAt
              ? "Test admin-confirmed"
              : "All scribes have completed this test"}
          </Typography>
          <Typography variant="body2" sx={{ display: "block" }}>
            Your score:{" "}
            <strong>
              {/* #1081 — test-aware decimal count (2 for freestyle, 1 otherwise). */}
              {formatPoints(totals.totalPoints, test)} / {formatPoints(totals.maxPoints, test)}{" "}
              = {formatPercentage(totals.percentage)}
            </strong>
          </Typography>
          {ride?.adminConfirmedAt && ride?.compositePercentage != null && (
            <Typography variant="body2" sx={{ display: "block", mt: 0.5 }}>
              Composite percentage across all judge positions:{" "}
              <strong>{formatPercentage(ride.compositePercentage)}</strong>
            </Typography>
          )}
        </Box>
      )}

      {/* #698 — a server-rejected save (409: a stale tab that already
          confirmed/signed, or any other write failure) surfaces here
          instead of failing silently. Clears itself on the next successful
          autosave (useScoreSheet resets saveError to null). */}
      {saveError && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="save-error-alert">
          {saveError}
        </Alert>
      )}

      {/* #698 — "score confirmed" banner: shown for THIS judge's sheet from
          the moment Confirm Score succeeds through to (and past) signing,
          independent of the ride-level allSheetsCompletedAt banner above
          (which needs every judge done). #1697 — `totals` (resolveScoreSheetTotals,
          live off local sheet.errors/eliminations for the common case) is the
          source of the displayed totalPoints/maxPoints/percentage; sheet.totalPoints
          etc. are not read directly here, so errors/eliminations changed after
          confirm recalculate on screen immediately, with no wait for a
          confirm-marks round-trip. #1891 — the one exception: once this
          sheet's frozen maxPoints disagrees with what `test` (a live catalog
          fetch) computes right now — the def was corrected after this sheet
          was scored — resolveScoreSheetTotals falls back to the sheet's own
          already-stored totals instead, so this banner never disagrees with
          report-defs/results.js/the USEF export on the same sheet. A further
          error/elimination edit in that window won't show on screen until
          the save round-trips (acceptable: it's rare, and never shows a
          number that isn't also true somewhere else). */}
      {isConfirmed && (
        <Box
          data-testid="marks-confirmed-banner"
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "success.lightest",
            border: 1,
            borderColor: "success.main",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={700}
            gutterBottom
            sx={{ color: "success.main" }}
          >
            {t("scoring.marksConfirmedTitle")}
          </Typography>
          <Typography variant="body2">
            {t("scoring.total", {
              // #1081 — same test-aware decimal count as the banner above.
              // #1697 — `totals` (resolveScoreSheetTotals, live off local
              // sheet.errors/eliminations for the common case) now wins over
              // sheet.totalPoints: errors/eliminations stay editable after
              // confirm (see numericMarksChanged in score-api), and `totals`
              // recomputes on every render while sheet.totalPoints is a
              // one-time snapshot stamped at confirm and only refreshed by a
              // full reload. Server round-trips through score-api's own
              // recompute once the save lands, so this and the persisted
              // value converge; this is what makes it converge WITHOUT that
              // round-trip on screen. #1891 — see resolveScoreSheetTotals'
              // own doc comment for the one case this no longer follows the
              // live def: a sheet whose frozen maxPoints disagrees with the
              // current Test.
              // The `totals` memo above (~line 901) returns
              // {totalPoints: 0, maxPoints: 0, percentage: 0} whenever
              // !sheet || !test, so these fields are always numbers here;
              // no sheet?.* fallback is needed or reachable.
              points: formatPoints(totals.totalPoints, test),
              max: formatPoints(totals.maxPoints, test),
              percentage: formatPercentage(totals.percentage),
            })}
          </Typography>
        </Box>
      )}

      <UtilityBanner
        events={utilityEvents}
        disabled={isCompleted}
        mySub={mySub}
        onClear={clearUtilityEvent}
      />

      {/* Card #1992 AC7/AC8/AC9/AC11 — active No Show alert, every panel
          position (see NoShowAlertBanner's own header comment). */}
      <NoShowAlertBanner
        noShow={rideNoShow.noShow}
        klass={klass}
        judgePosition={judgePosition}
        onUndo={rideNoShow.undo}
        undoing={rideNoShow.marking}
        undoError={rideNoShow.markError}
      />

      {/* Card #2064 — active Excused alert, every panel position (see
          ExcusedAlertBanner's own header comment). */}
      <ExcusedAlertBanner
        excused={rideExcused.excused}
        klass={klass}
        judgePosition={judgePosition}
        onUndo={rideExcused.undo}
        undoing={rideExcused.marking}
        undoError={rideExcused.markError}
      />

      {/* #317 — single "Test Overview" box combining the per-test prose
          items (Purpose / Introduce, and room for more). The items array is
          built from whatever fields the test carries, so the box lays them
          out and splits width by however many are present (#310 annotation
          preserved per item). */}
      {(test.purpose || test.introduce) && (
        <Box sx={{ mb: 1 }}>
          <TestOverviewBox
            title={t("scoring.testOverview")}
            disabled={isCompleted}
            onRequestClear={requestClearAnnotations}
            items={[
              test.purpose && {
                key: "purpose",
                label: t("scoring.purpose"),
                content: test.purpose,
                annotated: sheet?.annotatedPurpose,
                onChange: (annotatedPurpose) =>
                  setSheetFields({ annotatedPurpose }),
              },
              test.introduce && {
                key: "introduce",
                label: t("scoring.introduce"),
                content: test.introduce,
                annotated: sheet?.annotatedIntroduce,
                onChange: (annotatedIntroduce) =>
                  setSheetFields({ annotatedIntroduce }),
              },
            ].filter(Boolean)}
          />
        </Box>
      )}

      <Box
        sx={{
          mt: 1,
          bgcolor: "background.paper",
          borderRadius: 3,
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
        }}
      >
        {/* #221 — fixed layout with explicit column widths (colgroup) so
            the Remarks column always stays on-screen. The Movement-number
            column is dropped; Line + Position + Test sit under a single
            spanning "Test" heading. The type column is not displayed.
            #1449 — this last column also carries the elimination/error-of-
            course line markers and point deductions (not just free-text
            remarks — see the TableCell below), so it stays on screen even
            when commentMode is "generalOnly"; only the free-text
            RemarksInput inside it is conditionally hidden. */}
        <Table
          size="small"
          data-testid="movements-table"
          sx={{ tableLayout: "fixed", width: "100%" }}
        >
          {/* #221 — Line / Position / Test trimmed to give Remarks more
              room. */}
          <colgroup>
            <col style={{ width: "4%" }} />
            <col style={{ width: "9%" }} />
            {/* #1449 — under generalOnly the remarks column shrinks to just
                the markers/deductions it still carries (see the TableCell
                comment below), and the reclaimed width goes to the
                instruction (Test) and directive (Directives) columns,
                same trade the collectives table makes below. */}
            <col style={{ width: perMovementCommentsEnabled ? "18%" : "30%" }} />
            <col style={{ width: perMovementCommentsEnabled ? "19%" : "31%" }} />
            <col style={{ width: "6%" }} />
            {/* #256 — score column tightened to just fit "10.1 ×2"; the
                reclaimed width goes to remarks. */}
            <col style={{ width: "8%" }} />
            <col style={{ width: perMovementCommentsEnabled ? "36%" : "12%" }} />
          </colgroup>
          <TableHead>
            <TableRow>
              {/* #221 — "Test" spans the Line + Position + instruction
                  columns; left-aligned per review. */}
              <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                {t("scoring.test")}
              </TableCell>
              {/* #221 — "Directives" spans the directives text + the
                  per-row action icons (gavel + flag) column. */}
              <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                {t("scoring.directives")}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("scoring.score")}
              </TableCell>
              {/* #1449 — this header would otherwise label a column that,
                  under generalOnly, can only ever hold elimination/error
                  markers and deductions (never free-text remarks) — an
                  orphaned label. The cell itself stays (markers/deductions
                  still render there), only the text is conditional. */}
              <TableCell sx={{ fontWeight: 700 }}>
                {perMovementCommentsEnabled ? t("scoring.remarks") : ""}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderMovements.map((m, idx) => {
              // #761 — a section not assigned to THIS booth renders
              // read-only. Its values come from the assigned booth's own
              // sheet once that booth has completed it (useOtherSectionSubmission);
              // until then the row is simply blank + locked.
              const sectionLocked = sectionGated && !isMySectionEditable(m.section);
              const crossSheet = sectionLocked ? otherSheetForSection(m.section) : null;
              const entry = sectionLocked
                ? (crossSheet?.movementScores ?? []).find(
                    (row) => row.movementNumber === m.movementNumber,
                  ) ?? null
                : scoreByMovement.get(m.movementNumber);
              const isFlagged = entry?.flagged === true;
              // #221 — markers on the line where each penalty was raised,
              // removable from here: point-penalty errors of course, plus
              // every elimination attached to this row (manual reasons AND
              // the error-driven eliminate tier). The "E" badge is only for
              // errors of course.
              const lineMarkers = [
                ...errors
                  .filter((er) => er.movementNumber === m.movementNumber)
                  .map((er) => ({
                    id: er.id,
                    badge: true,
                    // #922 — a percent-scheme error of course carries `percent`,
                    // not `points`; render its unit so the row marker never
                    // reads "−undefined pt".
                    text: `${er.label} · −${
                      er.percent !== undefined
                        ? `${er.percent}%`
                        : `${er.points} pt`
                    }`,
                    onRemove: () => removeError(er.id),
                  })),
                ...eliminations
                  .filter((el) => el.movementNumber === m.movementNumber)
                  .map((el) => ({
                    id: el.id,
                    badge: Boolean(el.fromError),
                    text: el.fromError ? el.reason : `Eliminated: ${el.reason}`,
                    // #754 — Blood can't be undone: no remove action for it,
                    // unlike every other elimination reason.
                    onRemove:
                      el.reason === "Blood"
                        ? null
                        : () => removeElimination(el.id),
                  })),
              ];
              // #221 — per-row penalty state drives the gavel colour + the
              // left-border accent: red when this row carries an
              // elimination, amber for a point penalty (error/deduction),
              // grey when clean (like the flag).
              const rowEliminated = eliminations.some(
                (el) => el.movementNumber === m.movementNumber,
              );
              const rowHasPenalty =
                errors.some((er) => er.movementNumber === m.movementNumber) ||
                (entry?.deductions ?? []).length > 0;
              const gavelColor = rowEliminated
                ? "error.main"
                : rowHasPenalty
                ? "warning.main"
                : "action.disabled";
              // #499 — freestyle: render a labeled section header (Technical
              // Execution / Artistic Impression) at each section boundary. The
              // enrich (#498) orders movements technical-then-artistic, so a
              // header appears whenever this row's section differs from the
              // previous row's. Standard tests carry no section → no headers.
              const sectionLabelKey = m.section
                ? FREESTYLE_SECTION_LABEL_KEY[m.section]
                : null;
              const showSectionHeader =
                sectionLabelKey &&
                m.section !== renderMovements[idx - 1]?.section;
              // #761 — label a locked section's header with WHY it's
              // read-only: nothing submitted yet vs. already scored by the
              // assigned booth (AC2). resolvePositionForSection resolves the
              // assigned booth's arena position for the chip text.
              const otherPosition =
                m.section === "technical"
                  ? technicalOtherPosition
                  : m.section === "quality"
                  ? qualityOtherPosition
                  : null;
              const sectionReadOnlyLabel = sectionLocked
                ? crossSheet
                  ? t("scoring.sectionReadOnlySubmitted", { position: otherPosition ?? "?" })
                  : t("scoring.sectionReadOnlyAwaiting", { position: otherPosition ?? "?" })
                : null;
              // #675 — sub-group the TECHNICAL rows of a freestyle test by
              // gait (walk/trot/canter/other), purely visual: renderMovements
              // itself is untouched (catalog order stays the order of
              // record for scoring, autosave, and Tab/Enter field nav), a
              // divider just gets inserted wherever this row's derived gait
              // differs from the previous row's — same boundary technique
              // #499 uses for the technical/artistic section header above.
              const previousMovement = renderMovements[idx - 1];
              const movementGait =
                isFreestyleTest && m.section === "technical"
                  ? deriveMovementGait(m.description)
                  : null;
              const previousGait =
                isFreestyleTest && previousMovement?.section === "technical"
                  ? deriveMovementGait(previousMovement.description)
                  : null;
              const showGaitHeader =
                movementGait !== null && movementGait !== previousGait;
              return (
                <Fragment key={m.movementNumber}>
                  {showSectionHeader && (
                    <TableRow data-testid={`section-header-${m.section}`}>
                      <TableCell
                        colSpan={7}
                        sx={{
                          fontWeight: 700,
                          bgcolor: "action.hover",
                          color: "text.primary",
                        }}
                      >
                        {t(sectionLabelKey)}
                        {sectionReadOnlyLabel && (
                          <Typography
                            component="span"
                            variant="caption"
                            data-testid={`section-readonly-${m.section}`}
                            sx={{
                              ml: 1.5,
                              fontWeight: 400,
                              color: "text.secondary",
                            }}
                          >
                            {sectionReadOnlyLabel}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                  {showGaitHeader && (
                    <TableRow data-testid={`gait-header-${movementGait}`}>
                      <TableCell
                        colSpan={7}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.875rem",
                          color: "text.primary",
                          pl: 3,
                          pt: 2,
                          pb: 0.75,
                          // #675 — much stronger than the default 1px row
                          // divider so the scribe's eye catches the gait
                          // change without reading every row description.
                          // text.primary (not "divider") stays solid/opaque
                          // in dark mode too, unlike MUI's default grey.
                          borderTop: 3,
                          borderTopColor: "text.primary",
                        }}
                      >
                        {t(GAIT_LABEL_KEY[movementGait])}
                      </TableCell>
                    </TableRow>
                  )}
                <TableRow
                  hover
                  sx={{
                    // #156 — Remarks textfield grows vertically with
                    // content; anchor every cell to the top of the row
                    // so the other inputs don't drift to the middle
                    // when Remarks expand.
                    // #212 — paper-like spacing: roomier vertical padding
                    // so each movement reads as its own box like the
                    // printed test.
                    "& > .MuiTableCell-root": {
                      verticalAlign: "top",
                      py: 1.5,
                    },
                    // #221 — red left border on a row with an elimination;
                    // otherwise the warning-yellow flagged marker (R1-UI-019).
                    ...(rowEliminated
                      ? { borderLeft: 3, borderLeftColor: "error.main" }
                      : isFlagged && {
                          borderLeft: 3,
                          borderLeftColor: "warning.main",
                        }),
                  }}
                >
                  {/* #221 — three columns under the "Test" heading:
                      line number (#212), arena Position (#210, pipe-aware
                      #214), and the annotatable instruction (#211 / #214 /
                      #216). Movement number + type are not displayed. */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {idx + 1}
                    </Typography>
                  </TableCell>
                  {/* #221 — position wraps at spaces (so worded locations
                      like "Between C & H" fit) while short hyphenated codes
                      (K-X-M) stay whole; contents are centered. */}
                  <TableCell align="center">
                    <PipeText
                      text={m.location}
                      sx={{ fontWeight: 700, lineHeight: 1.4 }}
                    />
                  </TableCell>
                  <TableCell>
                    {/* #2080: clear control sits beside the annotatable
                        text, not inside it (AnnotatableText stays
                        markup-agnostic per #2079's guidance). */}
                    <Box display="flex" alignItems="flex-start">
                      <AnnotatableText
                        plain={m.description}
                        annotated={entry?.annotatedDescription}
                        disabled={isCompleted || sectionLocked}
                        onChange={(annotatedDescription) =>
                          setMovementScore(m.movementNumber, {
                            annotatedDescription,
                          })
                        }
                        sx={{ lineHeight: 1.6, flex: 1, minWidth: 0 }}
                      />
                      <AnnotationClearButton
                        annotated={entry?.annotatedDescription}
                        disabled={isCompleted || sectionLocked}
                        label={`${t("scoring.description")} (${t("scoring.line")} ${idx + 1})`}
                        onConfirm={() =>
                          setMovementScore(m.movementNumber, {
                            annotatedDescription: "",
                          })
                        }
                        onRequestClear={requestClearAnnotations}
                      />
                    </Box>
                  </TableCell>
                  {/* #211 — directives (DIRECTIVES) column. The
                      scribe-annotated markdown lives here.
                      #221 — wraps at word boundaries (DirectiveAnnotation
                      emits real spaces between tokens); overflowWrap only
                      breaks a single word that can't fit on its own line. */}
                  <TableCell
                    sx={{ whiteSpace: "normal", overflowWrap: "break-word" }}
                  >
                    {m.directive && (
                      <Box display="flex" alignItems="flex-start">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <DirectiveAnnotation
                            plain={m.directive}
                            annotated={entry?.annotatedDirective}
                            disabled={isCompleted || sectionLocked}
                            onChange={(annotatedDirective) =>
                              setMovementScore(m.movementNumber, {
                                annotatedDirective,
                              })
                            }
                          />
                        </Box>
                        <AnnotationClearButton
                          annotated={entry?.annotatedDirective}
                          disabled={isCompleted || sectionLocked}
                          label={`${t("scoring.directives")} (${t("scoring.line")} ${idx + 1})`}
                          onConfirm={() =>
                            setMovementScore(m.movementNumber, {
                              annotatedDirective: "",
                            })
                          }
                          onRequestClear={requestClearAnnotations}
                        />
                      </Box>
                    )}
                  </TableCell>
                  {/* #221 — per-row action icons (gavel menu + flag) in
                      their own column, grouped under the Directives
                      header. The gavel opens the elimination /
                      movement-error / deduction menu; the flag toggles
                      review. Neither changes appearance just because the
                      sheet is eliminated — the eliminated state lives in
                      the header chip + Penalties banner + the line where
                      it was raised. */}
                  <TableCell>
                    <Stack direction="row" spacing={0.25} alignItems="center">
                      <Tooltip title="Elimination / movement error">
                        <span>
                          <IconButton
                            size="small"
                            // #535 — secondary (mouse-driven) control kept out of
                            // the tab sequence so a scribe tabs straight down the
                            // mark inputs without stopping on the gavel/flag.
                            tabIndex={-1}
                            disabled={isCompleted || sectionLocked}
                            aria-label="Elimination or movement-error menu"
                            onClick={(e) =>
                              setRowMenu({
                                anchorEl: e.currentTarget,
                                movementNumber: m.movementNumber,
                              })
                            }
                            sx={{ p: 0.25 }}
                          >
                            {/* #221 — gavel mirrors the flag: red when this
                                row carries an elimination, amber for a
                                point penalty, grey when clean. */}
                            <GavelIcon
                              fontSize="small"
                              sx={{ color: gavelColor }}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={isFlagged ? "Remove flag" : "Flag for review"}
                      >
                        <span>
                          <IconButton
                            size="small"
                            tabIndex={-1} /* #535 — keep the mark tab flow clean */
                            disabled={isCompleted || sectionLocked}
                            onClick={() =>
                              setMovementScore(m.movementNumber, {
                                flagged: !isFlagged,
                              })
                            }
                            aria-label={
                              isFlagged ? "Remove flag" : "Flag for review"
                            }
                            sx={{ p: 0.25 }}
                          >
                            {isFlagged ? (
                              <FlagIcon fontSize="small" color="warning" />
                            ) : (
                              <OutlinedFlagIcon
                                fontSize="small"
                                sx={{ color: "action.disabled" }}
                              />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  {/* #221 — Score column: the score entry stays visible
                      even when eliminated (the status is shown elsewhere,
                      not stamped on every row). */}
                  <TableCell>
                    <ScoreEntry
                      value={entry?.score}
                      // #698 — a confirmed sheet's numeric marks are
                      // server-frozen one-way; disable + render green
                      // rather than let the scribe type a value the
                      // server will 409 on.
                      disabled={isCompleted || isConfirmed || sectionLocked}
                      locked={isConfirmed}
                      coefficient={m.coefficient}
                      // #712 / #889 — young-horse tests are scored to the
                      // tenth across every mark; otherwise only a freestyle
                      // ARTISTIC movement gets 0.1, all else half-point.
                      step={scoreStepFor(test, m.section)}
                      onChange={(score) =>
                        setMovementScore(m.movementNumber, { score })
                      }
                      // #668 — Tab/Enter keyboard flow.
                      inputRef={registerField(
                        markFieldKey(`movement-${m.movementNumber}`)
                      )}
                      onKeyDown={handleKeyNav(
                        markFieldKey(`movement-${m.movementNumber}`)
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    {/* #221 — markers on the line where each penalty was
                        raised: errors of course (with an "E" badge) and
                        eliminations (manual reasons + the eliminate tier).
                        Removable from here; red once eliminated. */}
                    {lineMarkers.map((le) => (
                      <Stack
                        key={le.id}
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{
                          mb: 0.5,
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          border: 1,
                          borderColor: eliminated ? "error.main" : "warning.main",
                          color: eliminated ? "error.main" : "warning.main",
                        }}
                      >
                        {le.badge && (
                          <Box
                            component="span"
                            aria-hidden
                            sx={{
                              fontWeight: 900,
                              border: 1,
                              borderRadius: 0.5,
                              px: 0.5,
                              fontSize: 11,
                              lineHeight: 1.5,
                            }}
                          >
                            E
                          </Box>
                        )}
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, flexGrow: 1 }}
                        >
                          {le.text}
                        </Typography>
                        {!isCompleted && !sectionLocked && le.onRemove && (
                          <Tooltip title="Remove error">
                            <IconButton
                              size="small"
                              tabIndex={-1} /* #535 */
                              aria-label="Remove error"
                              onClick={le.onRemove}
                              sx={{
                                p: 0,
                                color: eliminated ? "error.main" : "warning.main",
                              }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    ))}
                    {/* #209 / #221 — point deductions (one or more) shown
                        above the remarks text entry; each has its own X. */}
                    {(entry?.deductions ?? []).map((d) => (
                      <Stack
                        key={d.id}
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{
                          mb: 0.5,
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          border: 1,
                          borderColor: "warning.main",
                          color: "warning.main",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, flexGrow: 1 }}
                        >
                          −
                          {d.percent !== undefined
                            ? `${d.percent}%`
                            : `${d.points} pt`}
                          {d.reason ? `: ${d.reason}` : ""}
                        </Typography>
                        {!isCompleted && !sectionLocked && (
                          <Tooltip title="Remove deduction">
                            <IconButton
                              size="small"
                              tabIndex={-1} /* #535 */
                              aria-label="Remove deduction"
                              onClick={() =>
                                removeDeduction(m.movementNumber, d.id)
                              }
                              sx={{ p: 0, color: "warning.main" }}
                            >
                              <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    ))}
                    {/* #1449 — this def's commentMode is "generalOnly": no
                        per-movement free-text box, only the sheet-level
                        general comment at the bottom. The markers/
                        deductions above are unrelated to comments and keep
                        rendering regardless. Any remarks already stored on
                        this movement (movementScoreSchema.remarks is
                        untouched by this flag) are simply not shown or
                        editable here. */}
                    {perMovementCommentsEnabled && (
                    <RemarksInput
                      value={entry?.remarks}
                      disabled={isCompleted || sectionLocked}
                      onChange={(remarks) =>
                        setMovementScore(m.movementNumber, { remarks })
                      }
                      // #668 — Tab/Enter keyboard flow.
                      inputRef={registerField(
                        commentFieldKey(`movement-${m.movementNumber}`)
                      )}
                      onKeyDown={handleKeyNav(
                        commentFieldKey(`movement-${m.movementNumber}`)
                      )}
                    />
                    )}
                  </TableCell>
                </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* #226 — collective marks (type "C") render as a table mirroring
          the movement list above: Number, Test (name), Description
          (directive), Flag (review toggle — no elimination/gavel menu),
          Score (coefficient-aware, same ScoreEntry), Remarks. Driven by
          test.collectives (a typed list numbered from 1); scores keyed by
          the collective's movementNumber. #499 — freestyle tests have no
          collectives (their two sections render as grouped movements above),
          so the whole block is hidden when the list is empty. */}
      {test.collectives?.length > 0 && (
      <Box
        sx={{
          mt: 3,
          bgcolor: "background.paper",
          borderRadius: 3,
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ px: 2, pt: 2 }}
        >
          {test.collectivesLabel ?? t("scoring.collectiveMarks")}
        </Typography>
        <Table
          size="small"
          data-testid="collectives-table"
          sx={{ tableLayout: "fixed", width: "100%" }}
        >
          <colgroup>
            <col style={{ width: "6%" }} />
            <col style={{ width: perMovementCommentsEnabled ? "22%" : "37%" }} />
            <col style={{ width: perMovementCommentsEnabled ? "28%" : "43%" }} />
            <col style={{ width: "6%" }} />
            {/* #256 — match the movements table: tighter score column,
                wider remarks.
                #1449 — the collectives Remarks cell is comment-only (no
                markers/deductions, unlike the movements table), so the
                whole column drops when commentMode is "generalOnly"; the
                reclaimed width goes to Test/Description above. */}
            <col style={{ width: "8%" }} />
            {perMovementCommentsEnabled && <col style={{ width: "30%" }} />}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("scoring.number")}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("scoring.test")}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {t("scoring.description")}
              </TableCell>
              {/* flag column — no header, like the movement action column */}
              <TableCell aria-hidden />
              <TableCell sx={{ fontWeight: 700 }}>
                {t("scoring.score")}
              </TableCell>
              {perMovementCommentsEnabled && (
                <TableCell sx={{ fontWeight: 700 }}>
                  {t("scoring.remarks")}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {test.collectives.map((c) => {
              const entry = sheet?.collectives?.[c.movementNumber];
              const isFlagged = entry?.flagged === true;
              return (
                <TableRow
                  key={c.id ?? c.movementNumber}
                  hover
                  data-testid="collective-row"
                  sx={{
                    "& > .MuiTableCell-root": {
                      verticalAlign: "top",
                      py: 1.5,
                    },
                    ...(isFlagged && {
                      borderLeft: 3,
                      borderLeftColor: "warning.main",
                    }),
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {c.movementNumber}
                    </Typography>
                  </TableCell>
                  {/* #226 / #216 — Test (name) column is word-cycle
                      annotatable, like the movement instruction. */}
                  <TableCell>
                    {/* #2080: clear control beside the field, not inside
                        the markup-agnostic AnnotatableText. */}
                    <Box display="flex" alignItems="flex-start">
                      <AnnotatableText
                        plain={c.name}
                        annotated={entry?.annotatedName}
                        disabled={isCompleted}
                        onChange={(annotatedName) =>
                          setCollective(c.movementNumber, { annotatedName })
                        }
                        sx={{ fontWeight: 600, lineHeight: 1.4, flex: 1, minWidth: 0 }}
                      />
                      <AnnotationClearButton
                        annotated={entry?.annotatedName}
                        disabled={isCompleted}
                        label={`${t("scoring.test")} (${c.name})`}
                        onConfirm={() =>
                          setCollective(c.movementNumber, { annotatedName: "" })
                        }
                        onRequestClear={requestClearAnnotations}
                      />
                    </Box>
                  </TableCell>
                  {/* #226 / #216 — Description (directive) column uses the
                      same per-word annotation as the movement directives. */}
                  <TableCell
                    sx={{ whiteSpace: "normal", overflowWrap: "break-word" }}
                  >
                    {c.directive && (
                      <Box display="flex" alignItems="flex-start">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <DirectiveAnnotation
                            plain={c.directive}
                            annotated={entry?.annotatedDirective}
                            disabled={isCompleted}
                            onChange={(annotatedDirective) =>
                              setCollective(c.movementNumber, { annotatedDirective })
                            }
                          />
                        </Box>
                        <AnnotationClearButton
                          annotated={entry?.annotatedDirective}
                          disabled={isCompleted}
                          label={`${t("scoring.description")} (${c.name})`}
                          onConfirm={() =>
                            setCollective(c.movementNumber, { annotatedDirective: "" })
                          }
                          onRequestClear={requestClearAnnotations}
                        />
                      </Box>
                    )}
                  </TableCell>
                  {/* #226 — flag-for-review only; collectives have no
                      elimination / movement-error menu. */}
                  <TableCell>
                    <Tooltip
                      title={isFlagged ? "Remove flag" : "Flag for review"}
                    >
                      <span>
                        <IconButton
                          size="small"
                          tabIndex={-1} /* #535 — keep the mark tab flow clean */
                          disabled={isCompleted}
                          onClick={() =>
                            setCollective(c.movementNumber, {
                              flagged: !isFlagged,
                            })
                          }
                          aria-label={
                            isFlagged ? "Remove flag" : "Flag for review"
                          }
                          sx={{ p: 0.25 }}
                        >
                          {isFlagged ? (
                            <FlagIcon fontSize="small" color="warning" />
                          ) : (
                            <OutlinedFlagIcon
                              fontSize="small"
                              sx={{ color: "action.disabled" }}
                            />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <ScoreEntry
                      value={entry?.score}
                      // #698 — same server-frozen numeric lock as the
                      // movement ScoreEntry above.
                      disabled={isCompleted || isConfirmed}
                      locked={isConfirmed}
                      coefficient={c.coefficient}
                      // #889 — young-horse collectives are scored to the tenth
                      // too; every other test keeps the half-point default.
                      step={scoreStepFor(test)}
                      onChange={(score) =>
                        setCollective(c.movementNumber, { score })
                      }
                      // #668 — Tab/Enter keyboard flow.
                      inputRef={registerField(
                        markFieldKey(`collective-${c.movementNumber}`)
                      )}
                      onKeyDown={handleKeyNav(
                        markFieldKey(`collective-${c.movementNumber}`)
                      )}
                    />
                  </TableCell>
                  {/* #1449 — collectives Remarks is comment-only content
                      (unlike movements, no markers/deductions share this
                      cell), so the whole column drops when the def's
                      commentMode is "generalOnly". Any remarks already
                      stored on this collective (collectiveScoreSchema.
                      remarks is untouched by this flag) are simply not
                      shown or editable here. */}
                  {perMovementCommentsEnabled && (
                  <TableCell>
                    <RemarksInput
                      value={entry?.remarks}
                      disabled={isCompleted}
                      onChange={(remarks) =>
                        setCollective(c.movementNumber, { remarks })
                      }
                      // #668 — Tab/Enter keyboard flow.
                      inputRef={registerField(
                        commentFieldKey(`collective-${c.movementNumber}`)
                      )}
                      onKeyDown={handleKeyNav(
                        commentFieldKey(`collective-${c.movementNumber}`)
                      )}
                    />
                  </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      )}

      <Box
        sx={{
          mt: 3,
          p: 2.5,
          bgcolor: "background.paper",
          borderRadius: 3,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {t("scoring.generalRemarks")}
        </Typography>
        {/* #208 / #221 — elimination + error penalties above the remarks
            text entry. */}
        {(eliminated || errors.length > 0) && (
          <Box sx={{ mb: 1.5 }}>
            <PenaltiesBanner
              eliminations={eliminations}
              errors={errors}
              onRemoveElimination={removeElimination}
              onRemoveError={removeError}
              disabled={isCompleted}
            />
          </Box>
        )}
        <RemarksInput
          // #1888 — stable selector for the one field on the sheet that
          // commits per keystroke (score boxes commit on blur), which is what
          // scoring-form-no-remount.cy.js needs to exercise the debounced
          // autosave with focus still in the field.
          testId="general-remarks"
          value={sheet?.generalRemarks ?? ""}
          disabled={isCompleted}
          onChange={(v) => setGeneralRemarks(v)}
          placeholder="Enter general remarks..."
          maxLength={REMARKS_MAX_GENERAL}
          minRows={2}
          // #668 — last field in the sequence: Enter here is a no-op
          // (useSheetFieldNav refuses to move focus past the end), so it
          // can never walk onto "Complete and sign" below.
          inputRef={registerField(GENERAL_REMARKS_KEY)}
          onKeyDown={handleKeyNav(GENERAL_REMARKS_KEY)}
        />
      </Box>

      {/* #735 — signature box at the bottom of the sheet, once signed.
          Shared with scoreadmin-fe via common-fe so both surfaces render
          identically. #1824 — a composite-judge sheet's getScoreSheetSignature
          response carries `composite.judges[]` (both real judges, resolved
          through #1342's shared resolver server-side) instead of the ordinary
          single judgeName/url pair — render one SignatureBox per real judge
          rather than the composite roster row's run-together name. A judge
          entry with no url (not yet resolvable — #1342 AC6 data drift)
          degrades to nothing, same as SignatureBox's own documented
          behavior for the ordinary case. */}
      {isCompleted && sheetSignature?.composite && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
          divider={<Box sx={{ width: 1, borderLeft: { sm: 1 }, borderColor: "divider" }} />}
        >
          {sheetSignature.composite.judges.map((j) => (
            <SignatureBox
              key={j.role}
              judgeName={j.judgeName}
              url={j.url}
              loading={sheetSignatureLoading}
              label={t("scoring.signatureBoxLabel")}
            />
          ))}
        </Stack>
      )}
      {isCompleted && !sheetSignature?.composite && (
        <SignatureBox
          judgeName={sheetSignature?.judgeName}
          url={sheetSignature?.url}
          loading={sheetSignatureLoading}
          label={t("scoring.signatureBoxLabel")}
        />
      )}

      {/* #175 — bottom calculation Typography removed; running-total
          + percentage lived in the header chip row (#161 / #175), then
          moved down here (#725 — reverted #681's oversized header
          readout; the running score is now a small aside next to the
          Confirm Score / Sign & Complete controls, not the headline
          reading of the screen).
          #174 / #177 — footer pinned to the bottom of the viewport.
          Negative mb absorbs AppShell's inner Box bottom padding so
          the footer sits flush with the viewport bottom (no scrolling
          gap below the bar). Negative mx + re-applied px stretches
          edge-to-edge. */}
      <Box
        data-testid="scoring-footer"
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 1100,
          mt: 3,
          mx: { xs: -2, sm: -4 },
          mb: { xs: -2, sm: -4 },
          px: { xs: 2, sm: 4 },
          py: 1.5,
          bgcolor: "background.default",
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* #671 — bottom-left: the scribe's own tab order, opposite the
            Complete & Sign button. The label always names the mode that is
            ACTIVE RIGHT NOW, so a scribe can read the current state at a
            glance instead of flipping it to find out. Not gated on
            isCompleted: it's a personal input habit, not sheet data, and a
            scribe may flip it mid-show. */}
        <Box
          sx={{
            mr: "auto",
            display: "flex",
            alignItems: "center",
            gap: 2,
            minWidth: 0,
          }}
        >
          <FormControlLabel
            sx={{ ml: 0, mr: 0 }}
            control={
              <Switch
                // Brand theme (ThemeBrand.js) already forces the checked
                // Switch to foxOrange in BOTH schemes — primary.main is
                // brand darkGrey and is invisible on dark, so do NOT set
                // color="primary" here.
                checked={fieldOrder === COMMENT_FIRST}
                onChange={handleFieldOrderChange}
                inputProps={{
                  "aria-label": t("scoring.tabOrderAria"),
                  "data-cy": "tab-order-switch",
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {fieldOrder === COMMENT_FIRST
                  ? t("scoring.tabOrderCommentFirst")
                  : t("scoring.tabOrderMarkFirst")}
              </Typography>
            }
          />
          {/* #208 / #221 — elimination + error penalties at the bottom. */}
          {(eliminated || errors.length > 0) && (
            <Box sx={{ minWidth: 0 }}>
              <PenaltiesBanner
                eliminations={eliminations}
                errors={errors}
                onRemoveElimination={removeElimination}
                onRemoveError={removeError}
                disabled={isCompleted}
              />
            </Box>
          )}
        </Box>
        {/* #725 — running score, moved down here from the header and sized
            down (reverting #681's headline-scale enlargement) so it reads
            as a live aside next to approve/sign, not the final score. The
            small-print "running score" label exists specifically so it
            isn't mistaken for one. Hidden once eliminated — the
            "Eliminated" chip at the top already says the status. */}
        {!eliminated && (
          <Stack spacing={0} sx={{ flexShrink: 0 }}>
            <Typography
              variant="caption"
              data-testid="running-score-label"
              sx={{
                color: "text.secondary",
                lineHeight: 1.2,
                textTransform: "uppercase",
                fontSize: "0.625rem",
                fontWeight: 700,
              }}
            >
              running score
            </Typography>
            <Stack direction="row" spacing={2} sx={{ alignItems: "baseline" }}>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "baseline" }}
              >
                <Typography
                  component="span"
                  data-testid="running-score-points"
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 700,
                    lineHeight: 1,
                    // #725 — reverted #681's 2.25/2.5/3rem headline scale;
                    // this is a footer aside now, not the thing a scribe
                    // reads at a glance across a ring.
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    color: "text.primary",
                  }}
                >
                  {/* #1081 — test-aware decimal count. */}
                  {formatPoints(netEarned, test)}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "monospace",
                    lineHeight: 1,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                  }}
                >
                  / {formatPoints(footerPossible, test)}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={0.25}
                sx={{ alignItems: "baseline" }}
              >
                <Typography
                  component="span"
                  data-testid="running-score-percentage"
                  sx={{
                    fontFamily: "monospace",
                    fontWeight: 700,
                    lineHeight: 1,
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    color: "text.primary",
                  }}
                >
                  {runningPercentageText}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "monospace",
                    lineHeight: 1,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                  }}
                >
                  %
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        )}
        {/* R1-UI-015 — Save Draft removed; every state change autosaves
            via useScoreSheet's in-memory store. Bail-out via breadcrumbs.
            #698 — two-stage footer: Confirm Score (stage 1, gated by the
            zero/missing/flagged validation) then Sign & Complete (stage 2,
            gated on isConfirmed — no revalidation, it already happened).
            #733 — Sign & Complete is ALSO gated on every class judge having
            a show-wide signature on file (judge-sig E). */}
        {/* #1824 (AC4): when the class judge is a COMPOSITE roster row, the
            row itself is not who anyone signs for, so naming it would send the
            secretary after a person who doesn't exist. Name the REAL judge(s)
            still missing a signature instead (resolved via #1342's resolver
            above). Both keys are static literals; the unnamed #733 wording is
            the fallback when a missing judgeId has no roster name at all. */}
        {!isCompleted && hasMissingSignature && (
          <Alert severity="error" sx={{ mb: 1.5, fontWeight: 700 }}>
            {canNameMissingJudges
              ? t("scoring.missingJudgeSignatureNamed", {
                  judges: missingSignatureJudgeNames.join(", "),
                })
              : t("scoring.missingJudgeSignature")}
          </Alert>
        )}
        {/* #901 — a reopened sheet that was already confirmed/signed before
            (wasEverSigned, durable across reopen — see useScoreSheet) gets a
            single one-tap "Save" instead of the two-stage footer below, and
            skips both freeze dialogs. A sheet that has never been signed
            always falls through to the normal Confirm Score / Complete &
            Sign flow, unchanged. */}
        {wasEverSigned && !isCompleted ? (
          <Stack direction="row" spacing={1}>
            {/* #515 — MD3 convention (#514): native MUI 7 `loading` prop
                (spinner + auto-disable) instead of the manual startIcon +
                disabled + ternary-label pattern this used to be. */}
            <Button
              variant="contained"
              color="success"
              loading={isSaving}
              disabled={hasMissingSignature}
              onClick={handleSave}
              data-testid="save-button"
            >
              {t("scoring.saveButton")}
            </Button>
          </Stack>
        ) : isCompleted ? (
          // #663 — post-sign action. No more auto-navigate on sign (real-scribe
          // feedback: bouncing straight back to the class list was jarring) —
          // the scribe explicitly picks "Back to class" and selects the next
          // ride off the running order there. #1989 removed the sibling "Next
          // ride in class" button (reported not reliably landing on the next
          // ride).
          <Stack direction="row" spacing={1}>
            {/* #1989 (judge finding) — color="inherit": bare "primary" is
                #363636 in BOTH light and dark colorSchemes (ThemeBrand.js),
                dim-grey-on-dark. This button is now the SOLE post-sign
                action on a kiosk with no escape hatch, so its legibility is
                load-bearing (same fix as #1935, precedent at
                RideList.jsx:411-427). */}
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleBackToClass}
              data-testid="back-to-class-button"
            >
              {t("scoring.backToClass")}
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1}>
            {/* #515 — MD3 convention (#514): native MUI 7 `loading` prop. */}
            <Button
              variant="contained"
              color={eliminated ? "error" : "primary"}
              loading={isConfirmingMarks}
              disabled={isCompleted || isConfirmed}
              onClick={handleConfirmScore}
              data-testid="confirm-score-button"
            >
              {eliminated ? "Confirm elimination" : t("scoring.confirmScoreButton")}
            </Button>
            {/* #515 — the sign dialog closes synchronously on click, so the
                write itself (finalize()/complete(), via handleSign) plays out
                after this trigger button is the only thing still on screen;
                wire the loading state here rather than on the dialog's own
                (already-unmounted-by-then) Sign button. */}
            <Button
              variant="contained"
              color="success"
              loading={isSigning}
              disabled={
                isCompleted || !isConfirmed || isConfirmingMarks || hasMissingSignature
              }
              onClick={() => setSignConfirmOpen(true)}
              data-testid="open-sign-dialog-button"
            >
              {t("scoring.completeSign")}
            </Button>
          </Stack>
        )}
      </Box>

      {/* #698 — Confirm Score: the irreversible stage-1 gate. Plain-language
          warning (freezes numbers one-way, releases the score to the
          announcer + FVD) before calling confirmMarks(). There is
          deliberately no matching "unconfirm" dialog/button anywhere. */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {eliminated
            ? "Confirm elimination?"
            : t("scoring.confirmScoreTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {eliminated
              ? `This ride will be recorded as eliminated (${
                  eliminations.map((e) => e.reason).join("; ") ||
                  "no reason given"
                }). This is permanent — the marks freeze and cannot be edited again. Comments can still be added until the sheet is signed.`
              : t("scoring.confirmScoreBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={isConfirmingMarks}
            onClick={() => setConfirmDialogOpen(false)}
          >
            {t("scoring.confirmScoreCancel")}
          </Button>
          <Button
            variant="contained"
            color={eliminated ? "error" : "success"}
            disabled={isConfirmingMarks}
            onClick={handleConfirmMarks}
          >
            {eliminated
              ? "Confirm elimination"
              : t("scoring.confirmScoreConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pendingFlags.length > 0}
        onClose={() => setPendingFlags([])}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Resolve before confirming</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {flaggedCount > 0
              ? "Some rows still need attention. Tick the items below to confirm they are intentional, and clear any flags on the sheet."
              : "Tick each row below to confirm it is intentional before confirming the score."}
          </DialogContentText>
          <Stack spacing={0.5}>
            {pendingFlags.map((f) =>
              f.kind === "flagged" ? (
                // #171 — flagged rows are not acknowledgeable here. The
                // scribe has to Go back and clear the flag on the row.
                <Typography
                  key={f.key}
                  variant="body2"
                  color="warning.main"
                  sx={{ py: 0.5 }}
                >
                  {f.label} is flagged — clear the flag on the row to
                  continue.
                </Typography>
              ) : (
                <FormControlLabel
                  key={f.key}
                  control={
                    <Checkbox
                      checked={acknowledgedFlags.has(f.key)}
                      onChange={(e) => {
                        setAcknowledgedFlags((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(f.key);
                          else next.delete(f.key);
                          return next;
                        });
                      }}
                      sx={{
                        // #170 / #172 — both unchecked and checked states
                        // render dim in dark mode with MUI defaults.
                        // Unchecked → text.primary so the empty box is
                        // visible; checked → success.main so the filled
                        // affirmation reads as "intentional, good".
                        color: "text.primary",
                        "&.Mui-checked": { color: "success.main" },
                      }}
                    />
                  }
                  label={
                    f.kind === "zero"
                      ? `${f.label} scored 0 — intentional`
                      : `${f.label} not scored — intentional`
                  }
                />
              ),
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {/* #172 — default text Button color is primary.main, which
              renders dim in dark mode; color="inherit" picks up the
              dialog's text color so the secondary action stays legible. */}
          <Button color="inherit" onClick={() => setPendingFlags([])}>
            Go back
          </Button>
          <Button
            variant="contained"
            disabled={!canProceedFromResolve}
            onClick={() => {
              setPendingFlags([]);
              setConfirmDialogOpen(true);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* #171 — final sign-confirmation modal. Always shown before
          finalize(); the resolve dialog only acknowledges the rows,
          and this is where the "permanent, one-way trip" warning
          (meeting notes 2026-06-16:146) actually lives. */}
      <Dialog
        open={signConfirmOpen}
        onClose={() => setSignConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {eliminated ? "Confirm elimination?" : "Sign sheet?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {eliminated
              ? `This ride will be recorded as eliminated (${
                  eliminations.map((e) => e.reason).join("; ") ||
                  "no reason given"
                }) instead of scored. Once signed, the sheet is locked and cannot be edited or undone.`
              : "Once signed, this sheet is locked. The scores are committed to the test and cannot be edited or undone."}
          </DialogContentText>
          {/* #733 (judge-sig E) — no per-sheet signature capture anymore;
              finalize adopts the class judge's show-wide signature on
              file (captured in scoreadmin, #732). The Sign & Complete
              button that opens this dialog is already disabled while
              `hasMissingSignature` is true. */}
        </DialogContent>
        <DialogActions>
          {/* #172 — same dim-text-button fix as Go back above. */}
          <Button color="inherit" onClick={() => setSignConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={eliminated ? "error" : "primary"}
            onClick={handleSign}
            data-testid="sign-confirm-button"
          >
            {eliminated ? "Confirm elimination" : "Sign"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* #208 / #209 / #701 — shared per-row action menu. Penalty items
          (error of course, point deduction) are listed first since they're
          the common case; elimination reasons (which mark the whole sheet
          eliminated) are below a divider, "Custom reason…" opens a
          free-text dialog. #701 reordered penalties-on-top /
          eliminations-below and relabeled the singular "Error of course"
          penalty item so it isn't confused with the plural "Errors of
          course" elimination preset. */}
      <Menu
        open={Boolean(rowMenu.anchorEl)}
        anchorEl={rowMenu.anchorEl}
        onClose={closeRowMenu}
      >
        <Typography
          variant="overline"
          sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
        >
          Record penalty
        </Typography>
        {/* #221 — error of course: applies the next tier from the test's
            configured penalty schedule. This is a PENALTY, not an
            elimination — label kept singular + qualified to disambiguate
            from the plural "Errors of course" elimination preset below.
            #1306 — hidden entirely when the resolved schedule is empty
            (materiale/DR134 has no error-of-course concept to offer). */}
        {hasErrorConcept && (
          <MenuItem onClick={recordError}>
            Error of course (penalty)
            {nextErrorTier ? ` (${describeTier(nextErrorTier)})` : ""}
          </MenuItem>
        )}
        {/* #209 — point deduction is distinct from elimination. Hidden on
            percent-scheme tests where all deductions are percentages. */}
        {/* #922 — a movement deduction, its unit driven by the class scheme:
            a percent off the final % on percent-scheme tests, raw points on
            points-scheme tests. Previously (#873 stopgap) this was HIDDEN on
            percent tests to stop a scribe entering a percent into a points
            field; now the control itself carries the right unit. */}
        <MenuItem onClick={openDeduction}>
          {isPercentErrorScheme ? "Percent deduction…" : "Point deduction…"}
        </MenuItem>
        <Divider />
        <Typography
          variant="overline"
          sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
        >
          Eliminate ride
        </Typography>
        {ELIMINATION_REASONS.map((reason) => (
          <MenuItem key={reason} onClick={() => eliminate(reason)}>
            {reason}
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            const mn = rowMenu.movementNumber;
            closeRowMenu();
            setCustomReason({ open: true, value: "", movementNumber: mn });
          }}
        >
          Custom reason…
        </MenuItem>
        <Divider />
        {/* #916 (part A) — Scratch / No Show: unlike the elimination presets
            above (a local sheet field), these set the RIDE's status via
            score-api immediately (behind a confirm dialog, since it's a
            live write, not something the autosave queues). */}
        <Typography
          variant="overline"
          sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
        >
          Scratch ride
        </Typography>
        <MenuItem
          data-testid="gavel-menu-scratch"
          onClick={() => openTermReasonConfirm(RIDE_TERM_REASONS.SCRATCH)}
        >
          {t("scoring.gavelTermReasonScratch")}
        </MenuItem>
        <MenuItem
          data-testid="gavel-menu-no-show"
          onClick={() => openTermReasonConfirm(RIDE_TERM_REASONS.NO_SHOW)}
        >
          {t("scoring.gavelTermReasonNoShow")}
        </MenuItem>
        {/* Card #1992 — AC1/AC2: offered ONLY at judge position C. Deliberately
            its own section (distinct header + item copy from "Scratch ride" >
            "No Show" above) so the two never read as the same action — see
            the #916 collision note on confirmTermReason. */}
        {judgePosition === PRESIDING_JUDGE_POSITION && (
          <>
            <Divider />
            <Typography
              variant="overline"
              sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
            >
              {t("scoring.gavelNoShowSectionHeader")}
            </Typography>
            <MenuItem
              data-testid="gavel-menu-ride-no-show"
              disabled={Boolean(rideNoShow.noShow?.active)}
              onClick={openNoShowConfirm}
            >
              {rideNoShow.noShow?.active
                ? t("scoring.gavelRideNoShowAlreadyMarked")
                : t("scoring.gavelRideNoShowMenuItem")}
            </MenuItem>
            {/* Card #2064 — Excused (DR123.3's fourth term reason), same
                C-only gate and shape as No Show just above. */}
            <MenuItem
              data-testid="gavel-menu-ride-excused"
              disabled={Boolean(rideExcused.excused?.active)}
              onClick={openExcusedConfirm}
            >
              {rideExcused.excused?.active
                ? t("scoring.gavelRideExcusedAlreadyMarked")
                : t("scoring.gavelRideExcusedMenuItem")}
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Card #1992 — confirm before marking the ride's ride-level No Show
          (waterfalls to every panel judge, AC8/AC9). Mirrors the Scratch/No
          Show term-reason dialog's shape, with ONE deliberate difference:
          Cancel/onClose are never disabled while the write is in flight —
          a scribe must always be able to back out (card requirement). */}
      <Dialog
        open={noShowConfirmOpen}
        onClose={closeNoShowConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("scoring.gavelRideNoShowConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("scoring.gavelRideNoShowConfirmBody")}
          </DialogContentText>
          {rideNoShow.markError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("scoring.gavelRideNoShowError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={closeNoShowConfirm}>
            {t("scoring.gavelRideNoShowCancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            loading={rideNoShow.marking}
            onClick={confirmNoShow}
            data-testid="gavel-ride-no-show-confirm-button"
          >
            {t("scoring.gavelRideNoShowConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Card #2064 — confirm before marking the ride's ride-level Excused
          (DR123.3's fourth term reason, waterfalls to every panel judge).
          Mirrors the No Show confirm dialog just above, same deliberate
          difference: Cancel/onClose are never disabled while the write is
          in flight. */}
      <Dialog
        open={excusedConfirmOpen}
        onClose={closeExcusedConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("scoring.gavelRideExcusedConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("scoring.gavelRideExcusedConfirmBody")}
          </DialogContentText>
          {rideExcused.markError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("scoring.gavelRideExcusedError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={closeExcusedConfirm}>
            {t("scoring.gavelRideExcusedCancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            loading={rideExcused.marking}
            onClick={confirmExcused}
            data-testid="gavel-ride-excused-confirm-button"
          >
            {t("scoring.gavelRideExcusedConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* #2080: shared "clear annotations" confirm dialog: every clear
          button (Test Overview items, movement description/directive,
          collective name/directive) opens this SAME dialog with its own
          {label, onConfirm} rather than each rendering its own. AC2: a
          stray tap can't silently destroy a judge's remarks; Cancel is
          never disabled so a scribe can always back out (same shape as the
          Mark Ride No Show dialog above). */}
      <Dialog
        open={clearAnnotationsTarget != null}
        onClose={closeClearAnnotationsConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("scoring.clearAnnotationsConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("scoring.clearAnnotationsConfirmBody", {
              label: clearAnnotationsTarget?.label ?? "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={closeClearAnnotationsConfirm}
            data-testid="clear-annotations-cancel-button"
          >
            {t("scoring.clearAnnotationsCancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmClearAnnotations}
            data-testid="clear-annotations-confirm-button"
          >
            {t("scoring.clearAnnotationsConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* #916 (part A) — confirm before scratching the ride via score-api
          with the picked term reason. Mirrors the Confirm Score dialog's
          shape (plain-language warning, Cancel/Confirm actions). */}
      <Dialog
        open={termReasonConfirm.open}
        onClose={isSettingTermReason ? undefined : closeTermReasonConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("scoring.gavelTermReasonConfirmTitle", {
            reason: termReasonConfirm.reason,
          })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("scoring.gavelTermReasonConfirmBody", {
              reason: termReasonConfirm.reason,
            })}
          </DialogContentText>
          {termReasonError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("scoring.gavelTermReasonError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={isSettingTermReason}
            onClick={closeTermReasonConfirm}
          >
            {t("scoring.gavelTermReasonConfirmCancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            loading={isSettingTermReason}
            onClick={confirmTermReason}
            data-testid="gavel-term-reason-confirm-button"
          >
            {t("scoring.gavelTermReasonConfirmConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* #208 — custom elimination reason. */}
      <Dialog
        open={customReason.open}
        onClose={() =>
          setCustomReason({ open: false, value: "", movementNumber: null })
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Custom elimination reason</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Reason"
            value={customReason.value}
            onChange={(e) =>
              setCustomReason((p) => ({ ...p, value: e.target.value.slice(0, 120) }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && customReason.value.trim()) {
                eliminate(customReason.value.trim(), customReason.movementNumber);
                setCustomReason({ open: false, value: "", movementNumber: null });
              }
            }}
            inputProps={{ maxLength: 120 }}
            sx={{ mt: 1, ...READABLE_TEXTFIELD_SX }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() =>
              setCustomReason({ open: false, value: "", movementNumber: null })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!customReason.value.trim()}
            onClick={() => {
              eliminate(customReason.value.trim(), customReason.movementNumber);
              setCustomReason({ open: false, value: "", movementNumber: null });
            }}
          >
            Eliminate
          </Button>
        </DialogActions>
      </Dialog>

      {/* #209 — movement-level point-deduction dialog. */}
      <Dialog
        open={deduction.open}
        onClose={closeDeduction}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {isPercentErrorScheme ? "Percent deduction" : "Point deduction"}
          {deduction.movementNumber ? ` · movement ${deduction.movementNumber}` : ""}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {isPercentErrorScheme
              ? "Record a reason and the percentage to subtract from the final score for this movement."
              : "Record a reason and the number of points to subtract for this movement."}
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              autoFocus
              fullWidth
              label="Reason"
              value={deduction.reason}
              onChange={(e) =>
                setDeduction((p) => ({
                  ...p,
                  reason: e.target.value.slice(0, 120),
                }))
              }
              inputProps={{ maxLength: 120 }}
              sx={READABLE_TEXTFIELD_SX}
            />
            <TextField
              fullWidth
              label={
                isPercentErrorScheme
                  ? "Percent to subtract"
                  : "Points to subtract"
              }
              value={deduction.points}
              onChange={(e) =>
                setDeduction((p) => ({
                  ...p,
                  // digits + single decimal point only
                  points: e.target.value.replace(/[^0-9.]/g, ""),
                }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") applyDeduction();
              }}
              inputProps={{ inputMode: "decimal", pattern: "[0-9.]*" }}
              sx={READABLE_TEXTFIELD_SX}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={closeDeduction}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={
              !deduction.reason.trim() ||
              !(Number(deduction.points) > 0)
            }
            onClick={applyDeduction}
          >
            Apply deduction
          </Button>
        </DialogActions>
      </Dialog>

      {/* #266 — removing a point deduction, an error of course, or a
          (non-Blood) elimination requires a reason before the removal
          commits; Cancel/empty leaves the entry in place. The reason rides
          the existing #571 changeReason -> audit-stream forensic pipeline
          (see applySheetWithReason in useScoreSheet). */}
      <Dialog
        open={removalReason.open}
        onClose={closeRemovalReason}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {removalReason.kind === "elimination"
            ? "Remove elimination"
            : removalReason.kind === "deduction"
            ? "Remove deduction"
            : "Remove error of course"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Record why this is being removed. The reason is kept with the
            sheet&apos;s audit trail.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Reason"
            value={removalReason.reason}
            onChange={(e) =>
              setRemovalReason((p) => ({
                ...p,
                reason: e.target.value.slice(0, 120),
              }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && removalReason.reason.trim())
                confirmRemoval();
            }}
            inputProps={{ maxLength: 120, "data-cy": "removal-reason-input" }}
            sx={READABLE_TEXTFIELD_SX}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={closeRemovalReason}
            data-cy="removal-reason-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!removalReason.reason.trim()}
            onClick={confirmRemoval}
            data-cy="removal-reason-confirm"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
}
